import { useState, useEffect, type ReactNode, type CSSProperties } from 'react';
import { TopBarProvider, useTopBarCtx, type TopBarOverride } from '../contexts/TopBarContext.js';
import { TopBar } from '../components/TopBar.js';
import { Sidebar } from '../components/Sidebar.js';
import { AppFooter } from '../components/AppFooter.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { ActionRow } from '../components/ui/ActionRow.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { ListItem } from '../components/ui/ListItem.js';
import { KpiPanel } from '../components/ui/KpiPanel.js';
import { Icon } from '../components/Icon.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Pill } from '../components/PillGroup.js';
import { Input } from '../components/ui/Input.js';
import { Textarea } from '../components/ui/Textarea.js';
import { RadioGroup } from '../components/ui/Radio.js';
import { Toggle } from '../components/ui/Toggle.js';
import { Modal, type ModalSize } from '../components/ui/Modal.js';
import { Drawer, type DrawerSide, type DrawerSize } from '../components/ui/Drawer.js';
import { useToast } from '../components/ui/Toast.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { Badge } from '../components/ui/Badge.js';
import { Avatar } from '../components/ui/Avatar.js';
import { Tabs } from '../components/ui/Tabs.js';
import { Select } from '../components/ui/Select.js';
import { DatePicker } from '../components/ui/DatePicker.js';
import { DataTable, type ColumnDef } from '../components/ui/DataTable.js';
import { RichTextEditor } from '../components/ui/RichTextEditor.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { KVRow } from '../components/ui/KVRow.js';
import { KVGrid } from '../components/ui/KVGrid.js';

// ── Shared primitives ─────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 style={{
      margin: '0 0 4px',
      fontFamily: 'var(--evo-font)',
      fontSize: '24px',
      fontWeight: 400,
      color: 'var(--evo-fg-1)',
      textTransform: 'uppercase',
      letterSpacing: '-0.01em',
    }}>{children}</h2>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <p style={{
      margin: '0 0 10px',
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.88px',
      textTransform: 'uppercase',
      color: 'var(--evo-fg-3)',
      fontFamily: 'var(--evo-font)',
    }}>{children}</p>
  );
}

const CELL: CSSProperties = {
  padding: '8px 12px',
  fontSize: '12px',
  fontFamily: 'var(--evo-font)',
  borderBottom: '1px solid var(--evo-stroke)',
  verticalAlign: 'top',
};

const HEAD_CELL: CSSProperties = {
  ...CELL,
  fontSize: '11px',
  color: 'var(--evo-fg-3)',
  fontWeight: 500,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
};

function PropsTable({ rows }: {
  rows: Array<{ prop: string; type: string; required?: boolean; description: string }>;
}) {
  return (
    <div style={{ marginTop: 24 }}>
      <Label>Props</Label>
      <div style={{ overflowX: 'auto', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--evo-bg-3)' }}>
            <tr>
              <th style={HEAD_CELL}>Prop</th>
              <th style={HEAD_CELL}>Type</th>
              <th style={HEAD_CELL}>Required</th>
              <th style={HEAD_CELL}>Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.prop} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--evo-bg-3)' }}>
                <td style={{ ...CELL, color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font-mono)' }}>{r.prop}</td>
                <td style={{ ...CELL, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font-mono)' }}>{r.type}</td>
                <td style={{ ...CELL, color: r.required ? 'var(--evo-amber)' : 'var(--evo-fg-4)' }}>{r.required ? 'Yes' : 'No'}</td>
                <td style={{ ...CELL, color: 'var(--evo-fg-2)' }}>{r.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShowcaseSection({ id, title, description, children }: {
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} style={{ paddingTop: 48, paddingBottom: 48, borderBottom: '1px solid var(--evo-stroke)' }}>
      <SectionTitle>{title}</SectionTitle>
      {description
        ? <p style={{ margin: '4px 0 28px', fontSize: '13px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>{description}</p>
        : <div style={{ marginBottom: 28 }} />}
      {children}
    </section>
  );
}

function Example({ variant, description, code, fullWidth, children }: {
  variant: string;
  description?: string;
  code: string;
  fullWidth?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <span style={{
          fontSize: '11px',
          fontWeight: 500,
          letterSpacing: '0.88px',
          textTransform: 'uppercase',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
        }}>Variant: {variant}</span>
        {description && (
          <span style={{ fontSize: '12px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
            — {description}
          </span>
        )}
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            color: 'var(--evo-cyan)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--evo-font)',
            padding: 0,
          }}
        >{open ? 'Hide code' : 'Show code'}</button>
      </div>

      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
        <div style={{ padding: fullWidth ? 0 : 24, background: 'var(--evo-bg-2)' }}>
          {children}
        </div>
        {open && (
          <div style={{ borderTop: '1px solid var(--evo-stroke)', padding: '12px 16px', background: 'var(--evo-bg-3)' }}>
            <pre style={{
              margin: 0,
              fontSize: '12px',
              fontFamily: 'var(--evo-font-mono)',
              color: 'var(--evo-fg-2)',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
              lineHeight: 1.6,
            }}>{code}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Colors ────────────────────────────────────────────────────────────────────

const COLOR_GROUPS = [
  { label: 'Surface', tokens: ['--evo-bg', '--evo-bg-2', '--evo-bg-3', '--evo-panel', '--evo-panel-strong', '--evo-stroke'] },
  { label: 'Brand', tokens: ['--evo-cyan', '--evo-cyan-12', '--evo-cyan-18', '--evo-amber', '--evo-steel'] },
  { label: 'Foreground', tokens: ['--evo-fg-1', '--evo-fg-2', '--evo-fg-3', '--evo-fg-4', '--evo-fg-on-cyan', '--evo-fg-on-amber'] },
  { label: 'Extended — Blue', tokens: ['--evo-blue-700', '--evo-blue-500', '--evo-blue-300', '--evo-blue-100'] },
  { label: 'Extended — Green', tokens: ['--evo-green-600', '--evo-green-300', '--evo-green-100'] },
  { label: 'Extended — Slate', tokens: ['--evo-slate-900', '--evo-slate-200', '--evo-slate-100'] },
  { label: 'Semantic', tokens: ['--evo-danger', '--evo-success', '--evo-warning', '--evo-accent'] },
];

function ColorSwatch({ token }: { token: string }) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return (
    <div>
      <div style={{
        height: 48,
        background: `var(${token})`,
        borderRadius: 'var(--evo-r)',
        border: '1px solid var(--evo-stroke)',
        marginBottom: 6,
      }} />
      <p style={{ margin: '0 0 2px', fontSize: '11px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)', fontWeight: 500 }}>
        {token.replace('--evo-', '')}
      </p>
      <p style={{ margin: 0, fontSize: '10px', fontFamily: 'var(--evo-font-mono)', color: 'var(--evo-fg-4)' }}>
        {value || '—'}
      </p>
    </div>
  );
}

function ColorsSection() {
  return (
    <ShowcaseSection id="colors" title="Colors" description="All --evo-* color tokens. Edit packages/tokens/tokens.css — changes propagate instantly via Vite HMR.">
      {COLOR_GROUPS.map(group => (
        <div key={group.label} style={{ marginBottom: 32 }}>
          <Label>{group.label}</Label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 12 }}>
            {group.tokens.map(t => <ColorSwatch key={t} token={t} />)}
          </div>
        </div>
      ))}
    </ShowcaseSection>
  );
}

// ── Typography ────────────────────────────────────────────────────────────────

const TYPE_SCALE = [
  { label: 'Display', token: '--evo-fs-display', px: 200, cap: 60 },
  { label: 'H1', token: '--evo-fs-h1', px: 45 },
  { label: 'H2', token: '--evo-fs-h2', px: 24 },
  { label: 'H3', token: '--evo-fs-h3', px: 18 },
  { label: 'Tab', token: '--evo-fs-tab', px: 16 },
  { label: 'Body', token: '--evo-fs-body', px: 13 },
  { label: 'Label', token: '--evo-fs-label', px: 12 },
  { label: 'Caption', token: '--evo-fs-caption', px: 11 },
  { label: 'Tiny', token: '--evo-fs-tiny', px: 10 },
];

function TypographySection() {
  return (
    <ShowcaseSection id="typography" title="Typography">
      <Label>Type Scale</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden', marginBottom: 32 }}>
        {TYPE_SCALE.map(({ label, token, px, cap }, i) => (
          <div
            key={token}
            style={{
              display: 'grid',
              gridTemplateColumns: '80px 160px 1fr',
              alignItems: 'center',
              padding: '12px 16px',
              gap: 16,
              borderBottom: i < TYPE_SCALE.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
            }}
          >
            <span style={{ fontSize: '11px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>{label}</span>
            <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font-mono)' }}>
              {token.replace('--evo-fs-', '')} · {px}px
            </span>
            <span style={{
              fontSize: `${Math.min(px, cap ?? px)}px`,
              fontFamily: 'var(--evo-font)',
              color: 'var(--evo-fg-1)',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              Bai Jamjuree
            </span>
          </div>
        ))}
      </div>

      <Label>Font Families</Label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[
          { token: '--evo-font', label: 'Body / UI', sample: 'The quick brown fox jumps' },
          { token: '--evo-font-mono', label: 'Mono / Code', sample: 'const x = 42; // runtime' },
        ].map(f => (
          <div key={f.token} style={{ padding: '16px 20px', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
            <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
              {f.token} · {f.label}
            </p>
            <p style={{ margin: 0, fontSize: '18px', fontFamily: `var(${f.token})`, color: 'var(--evo-fg-1)', lineHeight: 1.3 }}>
              {f.sample}
            </p>
          </div>
        ))}
      </div>
    </ShowcaseSection>
  );
}

// ── Spacing ───────────────────────────────────────────────────────────────────

const SPACING_TOKENS = [
  { label: 'space-1', value: '4px' },
  { label: 'space-2', value: '8px' },
  { label: 'space-3', value: '12px' },
  { label: 'space-4', value: '16px' },
  { label: 'space-5', value: '24px' },
  { label: 'space-6', value: '32px' },
  { label: 'space-7', value: '48px' },
  { label: 'space-8', value: '64px' },
];

const LAYOUT_TOKENS = [
  { token: '--evo-sidebar-w', value: '200px', desc: 'Sidebar expanded width' },
  { token: '--evo-content-pad-x', value: '32px', desc: 'Content horizontal padding' },
  { token: '--evo-subnav-h', value: '56px', desc: 'Sub-navigation bar height' },
];

function SpacingSection() {
  return (
    <ShowcaseSection id="spacing" title="Spacing & Layout">
      <Label>Spacing Scale</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden', marginBottom: 32 }}>
        {SPACING_TOKENS.map(({ label, value }, i) => (
          <div
            key={label}
            style={{
              display: 'grid',
              gridTemplateColumns: '100px 72px 1fr',
              alignItems: 'center',
              padding: '10px 16px',
              gap: 16,
              borderBottom: i < SPACING_TOKENS.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
            }}
          >
            <span style={{ fontSize: '11px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font-mono)' }}>{label}</span>
            <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>{value}</span>
            <div style={{
              height: 8,
              width: value,
              maxWidth: '100%',
              background: 'var(--evo-cyan-12)',
              borderLeft: '2px solid var(--evo-cyan)',
              borderRadius: '0 2px 2px 0',
            }} />
          </div>
        ))}
      </div>

      <Label>Layout Tokens</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
        {LAYOUT_TOKENS.map(({ token, value, desc }, i) => (
          <div
            key={token}
            style={{
              display: 'grid',
              gridTemplateColumns: '220px 72px 1fr',
              alignItems: 'center',
              padding: '10px 16px',
              gap: 16,
              borderBottom: i < LAYOUT_TOKENS.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
            }}
          >
            <span style={{ fontSize: '11px', color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font-mono)' }}>{token}</span>
            <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>{value}</span>
            <span style={{ fontSize: '11px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>{desc}</span>
          </div>
        ))}
      </div>
    </ShowcaseSection>
  );
}

// ── AppShell ──────────────────────────────────────────────────────────────────

function AppShellSection() {
  const mono: CSSProperties = { fontFamily: 'var(--evo-font-mono)', color: 'var(--evo-fg-2)' };
  const note: CSSProperties = { fontSize: '10px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' };

  return (
    <ShowcaseSection id="appshell" title="AppShell" description="Root wrapper — flex column with TopBar at top, Sidebar + main in the middle, AppFooter at bottom. Owns the sidebar collapsed state.">
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden', fontFamily: 'var(--evo-font)', fontSize: '11px' }}>
        {/* TopBar row */}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', borderBottom: '1px solid var(--evo-stroke)', height: 52 }}>
          <div style={{ borderRight: '1px solid var(--evo-stroke)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--evo-bg-2)', color: 'var(--evo-cyan)' }}>
            Logo
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', color: 'var(--evo-fg-3)' }}>
            <span>date string</span>
            <span>LlmSpendWidget</span>
          </div>
        </div>
        {/* Sidebar + hero row */}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
          <div style={{ borderRight: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '20px 0', color: 'var(--evo-fg-3)' }}>
            <span>Sidebar</span>
            <span style={note}>var(--evo-sidebar-w)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ borderBottom: '1px solid var(--evo-stroke)', height: 100, display: 'flex', alignItems: 'center', padding: '0 32px', gap: 16, color: 'var(--evo-fg-3)' }}>
              <span style={{ color: 'var(--evo-fg-4)' }}>heroIcon</span>
              <span>title</span>
              <span style={{ marginLeft: 'auto', color: 'var(--evo-fg-4)' }}>bottomActions</span>
            </div>
            <div style={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--evo-fg-4)', padding: 20 }}>
              {'{'} children — Routes {'}'}
            </div>
          </div>
        </div>
        {/* Footer row */}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', borderTop: '1px solid var(--evo-stroke)', height: 36 }}>
          <div style={{ borderRight: '1px solid var(--evo-stroke)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px 0 20px', background: 'var(--evo-bg-2)', color: 'var(--evo-fg-4)' }}>
            <span>v0.1.0</span>
            <span>&#8249;</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 32px', color: 'var(--evo-fg-4)' }}>
            last sync: Xm ago
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--evo-bg-3)', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)' }}>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', lineHeight: 1.6 }}>
          <span style={{ color: 'var(--evo-cyan)' }}>TopBar override pattern</span> — Each page calls{' '}
          <code style={mono}>useTopBarCtx().setOverride(&#123;...&#125;)</code> in a{' '}
          <code style={mono}>useEffect</code> to inject its title, icon, and actions into the shared TopBar.
          The <code style={mono}>TopBarProvider</code> lives inside AppShell so all routes share the same context.
          Cleanup on unmount: <code style={mono}>return {'() => setOverride({})'}</code>.
        </p>
      </div>

      <PropsTable rows={[
        { prop: 'children', type: 'ReactNode', required: true, description: 'Page content — typically a Routes element.' },
      ]} />
    </ShowcaseSection>
  );
}

// ── TopBar ────────────────────────────────────────────────────────────────────

function TopBarSection() {
  const variants: Array<{ label: string; desc: string; override: TopBarOverride; code: string }> = [
    {
      label: 'Standard',
      desc: '1 action — LeadsPage, EmailPage, ContactsPage',
      override: {
        title: 'Leads',
        heroIcon: 'bolt-large',
        heroIconColor: 'var(--evo-steel)',
        bottomActions: (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" tone="cyan" size="md">New lead</Button>
          </div>
        ),
      },
      code: `const { setOverride } = useTopBarCtx();

useEffect(() => {
  setOverride({
    title: t('leads.pageTitle'),
    heroIcon: 'bolt-large',
    heroIconColor: 'var(--evo-steel)',
    bottomActions: <button onClick={onNew}>New lead</button>,
  });
  return () => setOverride({});
}, [setOverride, t]);`,
    },
    {
      label: 'Multi-action',
      desc: '3 actions — LeadDetailPage',
      override: {
        title: 'Dental Studio Wildwood',
        subtitle: 'Dentist · Wildwood',
        heroIcon: 'bolt-large',
        heroIconColor: 'var(--evo-steel)',
        bottomActions: (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" tone="neutral" size="md">Re-analyze</Button>
            <Button variant="outline" tone="cyan" size="md">Convert to contact</Button>
            <Button variant="outline" tone="danger" size="md">Delete</Button>
          </div>
        ),
      },
      code: `setOverride({
  title: lead.businessName,
  subtitle: \`\${lead.type} · \${lead.city}\`,
  heroIcon: 'bolt-large',
  heroIconColor: 'var(--evo-steel)',
  bottomActions: (
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={onReanalyze}>Re-analyze</button>
      <button onClick={onConvert}>Convert to contact</button>
      <button onClick={onDelete}>Delete</button>
    </div>
  ),
});`,
    },
    {
      label: 'Minimal',
      desc: 'No actions — SettingsPage',
      override: {
        title: 'Settings',
        heroIcon: 'settings-large',
        heroIconColor: 'var(--evo-fg-3)',
      },
      code: `setOverride({
  title: t('settings.pageTitle'),
  heroIcon: 'settings-large',
  heroIconColor: 'var(--evo-fg-3)',
});`,
    },
    {
      label: 'Home greeting',
      desc: 'Auto greeting — DashboardPage (no setOverride call needed)',
      override: {
        title: 'Good afternoon, Eduardo',
      },
      code: `// DashboardPage does NOT call setOverride.
// TopBar.tsx auto-generates the greeting when pathname === '/':
if (pathname === '/') {
  defaultTitle = t(\`home.greeting.\${greetingKey()}\`, { name: 'Eduardo' });
}
// greetingKey() returns 'morning' | 'afternoon' | 'evening' by hour.`,
    },
  ];

  return (
    <ShowcaseSection id="topbar" title="TopBar" description="Fixed 152px header with 2 rows: row 1 (52px) shows date + spend widget; row 2 (100px) shows optional icon + title + actions.">
      {variants.map(v => (
        <Example key={v.label} variant={v.label} description={v.desc} code={v.code} fullWidth>
          <TopBarProvider initialOverride={v.override}>
            <TopBar collapsed={false} />
          </TopBarProvider>
        </Example>
      ))}

      <PropsTable rows={[
        { prop: 'title', type: 'string', description: 'Page title rendered as H1 (45px, uppercase). Auto-set on / without setOverride.' },
        { prop: 'subtitle', type: 'string', description: 'Secondary line below title (13px, muted). Use for contextual info like lead type · city.' },
        { prop: 'heroIcon', type: 'IconName', description: 'Ikonate icon name rendered at 56px left of the title.' },
        { prop: 'heroIconColor', type: 'string (CSS var)', description: 'Icon stroke color. Default: var(--evo-cyan). Use var(--evo-steel) for neutral, var(--evo-fg-3) for muted.' },
        { prop: 'bottomActions', type: 'ReactNode', description: 'Right side of row 2. Any React node — typically a flex row of buttons.' },
      ]} />
    </ShowcaseSection>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function SidebarSection() {
  return (
    <ShowcaseSection id="sidebar" title="Sidebar" description="Fixed left navigation. Width animates between 200px (expanded) and 80px (collapsed). Active state is handled automatically by react-router NavLink.">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Expanded — 200px', collapsed: false },
          { label: 'Collapsed — 80px', collapsed: true },
        ].map(({ label, collapsed }) => (
          <div key={label}>
            <Label>{label}</Label>
            <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden', display: 'flex' }}>
              <Sidebar collapsed={collapsed} />
              <div style={{
                flex: 1,
                padding: 16,
                fontSize: '12px',
                color: 'var(--evo-fg-4)',
                fontFamily: 'var(--evo-font)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--evo-bg-2)',
              }}>
                content area
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 16px', background: 'var(--evo-bg-3)', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', marginBottom: 8 }}>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', lineHeight: 1.6 }}>
          The Leads nav item shows a badge count from{' '}
          <code style={{ fontFamily: 'var(--evo-font-mono)', color: 'var(--evo-fg-2)' }}>useDueDraftsCount()</code>.
          In collapsed mode the badge becomes a dot indicator positioned at the icon corner.
        </p>
      </div>

      <PropsTable rows={[
        { prop: 'collapsed', type: 'boolean', required: true, description: 'Controls width: 200px when false, 80px when true. Animates with 180ms ease.' },
      ]} />
    </ShowcaseSection>
  );
}

// ── AppFooter ─────────────────────────────────────────────────────────────────

function AppFooterSection() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <ShowcaseSection id="appfooter" title="AppFooter" description="44px footer strip. Left zone mirrors sidebar width (version label + collapse toggle). Right zone shows last email sync timestamp.">
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
        <AppFooter collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
      </div>
      <p style={{ fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', marginTop: 8 }}>
        Click the arrow to toggle the collapsed state. Last sync time is fetched from the dashboard summary API.
      </p>

      <PropsTable rows={[
        { prop: 'collapsed', type: 'boolean', required: true, description: 'Mirrors AppShell sidebar state — adjusts left zone width and toggle arrow direction.' },
        { prop: 'onToggle', type: '() => void', required: true, description: 'Fired when user clicks the collapse/expand arrow. Wired up in AppShell.' },
      ]} />
    </ShowcaseSection>
  );
}

// ── Module Chrome ─────────────────────────────────────────────────────────────

const STAGE_OPTS = [
  { value: '', label: 'All stages' },
  { value: 'new', label: 'New', count: 12 },
  { value: 'analyzed', label: 'Analyzed', count: 34 },
  { value: 'outreach', label: 'Outreach', count: 18 },
  { value: 'closed_won', label: 'Closed won', count: 7 },
  { value: 'closed_lost', label: 'Closed lost', count: 5 },
];

const CAT_OPTS = [
  { value: '', label: 'All categories' },
  { value: 'A', label: 'Category A' },
  { value: 'B', label: 'Category B' },
  { value: 'C', label: 'Category C' },
  { value: 'D', label: 'Category D' },
];

function ModuleChromeSection() {
  const [ddSimple, setDdSimple] = useState<string | null>(null);
  const [ddCount, setDdCount] = useState<string | null>(null);
  const [ddSearch, setDdSearch] = useState<string | null>(null);
  const [ddMulti, setDdMulti] = useState<string[]>([]);

  const [frSearch, setFrSearch] = useState('');
  const [frStage, setFrStage] = useState<string | null>(null);
  const [frCat, setFrCat] = useState<string | null>(null);

  const [frChipSearch, setFrChipSearch] = useState('');
  const [frChipStage, setFrChipStage] = useState<string>('analyzed');
  const [frChipCat, setFrChipCat] = useState<string>('A');

  const hasFilters = frStage !== null || frCat !== null;

  const [sab1Visible, setSab1Visible] = useState(true);
  const [sab2Visible, setSab2Visible] = useState(true);
  const [_sab2Cat, setSab2Cat] = useState<string | null>(null);
  const [sab3Visible, setSab3Visible] = useState(true);
  const [sab4Visible, setSab4Visible] = useState(true);

  const [tabValue, setTabValue]   = useState<string>('inbox');
  const [tabSize, setTabSize]     = useState<'sm' | 'md'>('md');
  const [tabFw, setTabFw]         = useState<string>('all');

  return (
    <ShowcaseSection
      id="module-chrome"
      title="Module Chrome"
      description="Dropdown, FilterRow, ActionRow, and SelectionActionBar — the shared chrome for every module's filter bar, section headers, and bulk selection."
    >
      {/* ── Dropdown ── */}
      <Label>Dropdown</Label>

      <Example variant="simple" code={`<Dropdown\n  value={stage}\n  options={[{ value: 'new', label: 'New' }, ...]}\n  onChange={setStage}\n/>`}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Dropdown<string>
            value={ddSimple}
            placeholder="Pick option"
            options={[
              { value: 'opt1', label: 'Option 1' },
              { value: 'opt2', label: 'Option 2' },
              { value: 'opt3', label: 'Option 3' },
              { value: 'opt4', label: 'Option 4' },
              { value: 'opt5', label: 'Option 5' },
            ]}
            onChange={setDdSimple}
          />
          {ddSimple && (
            <button onClick={() => setDdSimple(null)} style={{ fontSize: '12px', color: 'var(--evo-fg-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--evo-font)' }}>
              Clear
            </button>
          )}
        </div>
      </Example>

      <Example variant="count badges" description="stage filter with item counts" code={`<Dropdown<string>\n  value={stage}\n  options={STAGES.map(s => ({ value: s.id, label: s.label, count: s.count }))}\n  onChange={setStage}\n/>`}>
        <Dropdown<string>
          value={ddCount}
          placeholder="All stages"
          options={STAGE_OPTS}
          onChange={setDdCount}
        />
      </Example>

      <Example variant="searchable" description="filters options as you type" code={`<Dropdown<string>\n  value={val}\n  options={opts}\n  onChange={setVal}\n  searchable\n/>`}>
        <Dropdown<string>
          value={ddSearch}
          placeholder="Search stages…"
          options={STAGE_OPTS.slice(1)}
          onChange={setDdSearch}
          searchable
        />
      </Example>

      <Example variant="multi-select" description="toggles values in/out" code={`<Dropdown<string>\n  multi\n  value={selected}\n  options={opts}\n  onChange={setSelected}\n/>`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Dropdown<string>
            multi
            value={ddMulti}
            placeholder="Select categories"
            options={['A', 'B', 'C', 'D'].map(c => ({ value: c, label: `Category ${c}` }))}
            onChange={setDdMulti}
          />
          <span style={{ fontSize: '12px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
            Selected: {ddMulti.length === 0 ? 'none' : ddMulti.join(', ')}
          </span>
        </div>
      </Example>

      <Example variant="size sm / md" description="sm=28px trigger, md=32px (default)" code={`<Dropdown size="sm" ... />\n<Dropdown size="md" ... />`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Dropdown<string> value="new" options={STAGE_OPTS.slice(1)} onChange={() => {}} size="sm" />
          <Dropdown<string> value="new" options={STAGE_OPTS.slice(1)} onChange={() => {}} size="md" />
        </div>
      </Example>

      <Example variant="disabled" code={`<Dropdown disabled value={null} options={opts} onChange={() => {}} />`}>
        <Dropdown<string> value="analyzed" options={STAGE_OPTS.slice(1)} onChange={() => {}} disabled />
      </Example>

      <PropsTable rows={[
        { prop: 'value', type: 'T | null', required: true, description: 'Selected value. null shows placeholder. Use T[] when multi=true.' },
        { prop: 'options', type: 'DropdownOption<T>[]', required: true, description: 'Options array. Each may include label, icon, count.' },
        { prop: 'onChange', type: '(value: T) => void', required: true, description: 'Called with the selected value. For multi, called with T[].' },
        { prop: 'placeholder', type: 'string', required: false, description: 'Trigger label shown when value is null.' },
        { prop: 'size', type: "'sm' | 'md'", required: false, description: "sm=28px, md=32px (default)." },
        { prop: 'align', type: "'left' | 'right'", required: false, description: 'Menu alignment relative to trigger (default left).' },
        { prop: 'searchable', type: 'boolean', required: false, description: 'Shows search input at top of menu.' },
        { prop: 'multi', type: 'boolean', required: false, description: 'Toggles multi-select mode. value must be T[].' },
        { prop: 'disabled', type: 'boolean', required: false, description: 'Disables trigger and prevents interaction.' },
      ]} />

      {/* ── FilterRow ── */}
      <Label>FilterRow</Label>

      <Example variant="standard" description="search + 2 dropdowns + clear action" fullWidth code={`<FilterRow>\n  <FilterRow.Search value={q} onChange={setQ} onSubmit={apply} onClear={clear} />\n  <FilterRow.Dropdown<string> value={stage} options={STAGES} onChange={setStage} />\n  <FilterRow.Dropdown<string> value={cat} options={CATS} onChange={setCat} />\n  <FilterRow.Spacer />\n  {(stage || cat) && <FilterRow.Action label="Clear filters" onClick={clearAll} />}\n</FilterRow>`}>
        <FilterRow>
          <FilterRow.Search
            value={frSearch}
            onChange={setFrSearch}
            onSubmit={() => {}}
            onClear={() => setFrSearch('')}
            placeholder="Search leads…"
            width={280}
          />
          <FilterRow.Dropdown<string>
            value={frStage}
            placeholder="All stages"
            options={STAGE_OPTS}
            onChange={(v) => setFrStage(v || null)}
          />
          <FilterRow.Dropdown<string>
            value={frCat}
            placeholder="All categories"
            options={CAT_OPTS}
            onChange={(v) => setFrCat(v || null)}
          />
          <FilterRow.Spacer />
          {hasFilters && (
            <FilterRow.Action label="Clear filters" onClick={() => { setFrStage(null); setFrCat(null); }} />
          )}
        </FilterRow>
      </Example>

      <Example variant="with active chips" description="chips show which filters are active" fullWidth code={`<FilterRow>\n  <FilterRow.Search value={q} onChange={setQ} />\n  <FilterRow.Chip label={\`Stage: \${stage}\`} onRemove={() => setStage(null)} />\n  <FilterRow.Chip label={\`Cat: \${cat}\`} onRemove={() => setCat(null)} />\n  <FilterRow.Spacer />\n  <FilterRow.Action label="Clear all" onClick={clearAll} />\n</FilterRow>`}>
        <FilterRow>
          <FilterRow.Search
            value={frChipSearch}
            onChange={setFrChipSearch}
            onClear={() => setFrChipSearch('')}
            placeholder="Search…"
            width={280}
          />
          <FilterRow.Chip
            label={`Stage: ${frChipStage}`}
            onRemove={() => setFrChipStage('')}
          />
          {frChipStage && frChipCat && (
            <FilterRow.Chip
              label={`Cat: ${frChipCat}`}
              onRemove={() => setFrChipCat('')}
            />
          )}
          <FilterRow.Spacer />
          <FilterRow.Action label="Clear all" onClick={() => { setFrChipSearch(''); setFrChipStage(''); setFrChipCat(''); }} />
        </FilterRow>
      </Example>

      <PropsTable rows={[
        { prop: 'borderBottom', type: 'boolean', required: false, description: 'Renders 1px stroke bottom border (default true).' },
        { prop: 'FilterRow.Search', type: 'component', required: false, description: 'Search input with optional submit + clear buttons.' },
        { prop: 'FilterRow.Dropdown', type: 'component', required: false, description: 'FilterRow-sized Dropdown wrapper (size=sm).' },
        { prop: 'FilterRow.Chip', type: 'component', required: false, description: 'Removable active-filter chip with × button.' },
        { prop: 'FilterRow.Spacer', type: 'component', required: false, description: 'flex-grow spacer to push right-side items.' },
        { prop: 'FilterRow.Action', type: 'component', required: false, description: 'Muted or cyan link button (Clear, Reset, etc.).' },
      ]} />

      {/* ── ActionRow ── */}
      <Label>ActionRow</Label>

      <Example variant="title + 1 action" code={`<ActionRow title="Recent emails">\n  <ActionRow.Action label="Mark all read" tone="muted" onClick={markAllRead} />\n</ActionRow>`}>
        <div style={{ padding: '0 24px' }}>
          <ActionRow title="Recent emails">
            <SectionHeaderAction tone="muted" onClick={() => {}}>Mark all read</SectionHeaderAction>
          </ActionRow>
        </div>
      </Example>

      <Example variant="title + count + 2 actions" code={`<ActionRow title="Outreach sequence" count={5}>\n  <ActionRow.Action label="Pause" tone="amber" onClick={pause} />\n  <ActionRow.Action label="Regenerate" tone="cyan-outline" onClick={regen} />\n</ActionRow>`}>
        <div style={{ padding: '0 24px' }}>
          <ActionRow title="Outreach sequence" count={5}>
            <SectionHeaderAction tone="amber" onClick={() => {}}>Pause</SectionHeaderAction>
            <SectionHeaderAction onClick={() => {}}>Regenerate</SectionHeaderAction>
          </ActionRow>
        </div>
      </Example>

      <Example variant="title + description + 3 actions" code={`<ActionRow\n  title="Analysis"\n  description="3 drafts pending, 2 sent"\n  borderBottom\n>\n  <ActionRow.Action label="Skip" tone="muted" />\n  <ActionRow.Action label="Pause" tone="amber" />\n  <ActionRow.Action label="Send all" tone="cyan" />\n</ActionRow>`}>
        <div style={{ padding: '0 24px' }}>
          <ActionRow title="Analysis" description="3 drafts pending · 2 sent" borderBottom>
            <SectionHeaderAction tone="muted" onClick={() => {}}>Skip</SectionHeaderAction>
            <SectionHeaderAction tone="amber" onClick={() => {}}>Pause</SectionHeaderAction>
            <SectionHeaderAction tone="cyan" onClick={() => {}}>Send all</SectionHeaderAction>
          </ActionRow>
        </div>
      </Example>

      <Example variant="loading state" code={`<ActionRow.Action label="Saving…" tone="cyan-outline" loading />`}>
        <div style={{ padding: '0 24px' }}>
          <ActionRow title="Saving example">
            <SectionHeaderAction tone="muted" disabled>Saved</SectionHeaderAction>
            <SectionHeaderAction loading loadingLabel="Saving…">Saving…</SectionHeaderAction>
          </ActionRow>
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'title', type: 'string', required: true, description: 'Section label rendered uppercase with letterspacing.' },
        { prop: 'count', type: 'number', required: false, description: 'Optional numeric suffix after the title.' },
        { prop: 'description', type: 'string', required: false, description: 'Secondary muted text after count.' },
        { prop: 'padding', type: 'string', required: false, description: "CSS padding shorthand (default '16px 0')." },
        { prop: 'borderBottom', type: 'boolean', required: false, description: 'Renders 1px stroke bottom border (default false).' },
        { prop: 'ActionRow.Action tone', type: "'cyan' | 'cyan-outline' | 'amber' | 'warn' | 'muted'", required: false, description: 'Button visual style. cyan=filled, others=outline/transparent.' },
        { prop: 'ActionRow.Action loading', type: 'boolean', required: false, description: 'Disables button and shows loadingLabel (falls back to label).' },
      ]} />

      {/* ── SelectionActionBar ── */}
      <Label>SelectionActionBar</Label>
      <p style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-fg-3)', margin: '0 0 16px' }}>
        Appears only when rows are checked. Slides down below the subnav at full panel width. Background uses <code>--evo-panel-strong</code>. Dropdowns inside float via portal (never clipped). Add <code>confirmRequired</code> to any Action for inline confirmation without browser dialogs.
      </p>

      <Example variant="3 simple actions" code={`<SelectionActionBar visible={n > 0} countLabel={\`\${n} selected\`} onClearSelection={clear}>\n  <SelectionActionBar.Action label="Mark as read" onClick={...} />\n  <SelectionActionBar.Action label="Follow up" tone="amber" onClick={...} />\n  <SelectionActionBar.Action label="Delete" tone="danger" onClick={...} />\n</SelectionActionBar>`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => setSab1Visible(v => !v)}
            style={{ alignSelf: 'flex-start', height: 28, padding: '0 12px', borderRadius: 4, border: '1px solid var(--evo-stroke)', background: 'transparent', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, cursor: 'pointer' }}
          >
            {sab1Visible ? 'Hide bar' : 'Show bar (1 selected)'}
          </button>
          <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 6, overflow: 'hidden' }}>
            <SelectionActionBar visible={sab1Visible} countLabel="1 selected" onClearSelection={() => setSab1Visible(false)}>
              <SelectionActionBar.Action label="Mark as read" onClick={() => {}} />
              <SelectionActionBar.Action label="Follow up" tone="amber" onClick={() => {}} />
              <SelectionActionBar.Action label="Delete" tone="danger" onClick={() => setSab1Visible(false)} />
            </SelectionActionBar>
            <div style={{ padding: '12px 16px', background: 'var(--evo-bg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, color: 'var(--evo-fg-3)' }}>Content area</div>
          </div>
        </div>
      </Example>

      <Example variant="with Dropdown + Dividers — direct apply, grouped actions (5 selected)" code={`<SelectionActionBar visible={n > 0} countLabel={\`\${n} selected\`} onClearSelection={clear}>\n  <Dropdown value={null} placeholder="Move to category" options={...}\n    onChange={val => { if (val) applyCategory(ids, val); }} size="sm" />\n  <SelectionActionBar.Divider />\n  <SelectionActionBar.Action label="Delete" tone="danger" confirmRequired onClick={deleteAll} />\n</SelectionActionBar>`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => setSab2Visible(v => !v)}
            style={{ alignSelf: 'flex-start', height: 28, padding: '0 12px', borderRadius: 4, border: '1px solid var(--evo-stroke)', background: 'transparent', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, cursor: 'pointer' }}
          >
            {sab2Visible ? 'Hide bar' : 'Show bar (5 selected)'}
          </button>
          <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 6, overflow: 'hidden' }}>
            <SelectionActionBar visible={sab2Visible} countLabel="5 selected" onClearSelection={() => { setSab2Visible(false); setSab2Cat(null); }}>
              <Dropdown<string>
                value={null}
                placeholder="Move to category"
                options={[
                  { value: 'leads', label: 'Leads' },
                  { value: 'newsletter', label: 'Newsletter' },
                  { value: 'support', label: 'Support' },
                ]}
                onChange={val => { setSab2Cat(val); setSab2Visible(false); }}
                size="sm"
              />
              <SelectionActionBar.Divider />
              <SelectionActionBar.Action label="Delete" tone="danger" confirmRequired onClick={() => setSab2Visible(false)} />
            </SelectionActionBar>
            <div style={{ padding: '12px 16px', background: 'var(--evo-bg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, color: 'var(--evo-fg-3)' }}>Content area</div>
          </div>
        </div>
      </Example>

      <Example variant="confirmRequired — inline confirm without browser dialogs (3 selected)" code={`<SelectionActionBar.Action\n  label="Delete selected"\n  tone="danger"\n  confirmRequired\n  onClick={deleteAll}\n/>`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => setSab3Visible(v => !v)}
            style={{ alignSelf: 'flex-start', height: 28, padding: '0 12px', borderRadius: 4, border: '1px solid var(--evo-stroke)', background: 'transparent', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, cursor: 'pointer' }}
          >
            {sab3Visible ? 'Hide bar' : 'Show bar (3 selected)'}
          </button>
          <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 6, overflow: 'hidden' }}>
            <SelectionActionBar visible={sab3Visible} countLabel="3 selected" onClearSelection={() => setSab3Visible(false)}>
              <SelectionActionBar.Action label="Mark as read" onClick={() => {}} />
              <SelectionActionBar.Action label="Delete selected" tone="danger" confirmRequired onClick={() => setSab3Visible(false)} />
            </SelectionActionBar>
            <div style={{ padding: '12px 16px', background: 'var(--evo-bg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, color: 'var(--evo-fg-3)' }}>Content area</div>
          </div>
        </div>
      </Example>

      <Example variant="highlighted dividers — 4 groups (email pattern, 8 selected)" code={`<SelectionActionBar visible={n > 0} countLabel={\`\${n} selected\`} onClearSelection={clear}>\n  <SelectionActionBar.Action label="Mark read" onClick={...} />\n  <SelectionActionBar.Action label="Mark unread" onClick={...} />\n  <SelectionActionBar.Divider />\n  <Dropdown value={null} placeholder="Categorize" options={...} onChange={applyCategory} size="sm" />\n  <SelectionActionBar.Divider />\n  <SelectionActionBar.Action label="Follow up" tone="amber" onClick={...} />\n  <SelectionActionBar.Action label="Remove follow-up" onClick={...} />\n  <SelectionActionBar.Divider />\n  <SelectionActionBar.Action label="Move to trash" tone="danger" confirmRequired onClick={...} />\n</SelectionActionBar>`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => setSab4Visible(v => !v)}
            style={{ alignSelf: 'flex-start', height: 28, padding: '0 12px', borderRadius: 4, border: '1px solid var(--evo-stroke)', background: 'transparent', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, cursor: 'pointer' }}
          >
            {sab4Visible ? 'Hide bar' : 'Show bar (8 selected)'}
          </button>
          <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 6, overflow: 'hidden' }}>
            <SelectionActionBar visible={sab4Visible} countLabel="8 selected" onClearSelection={() => setSab4Visible(false)}>
              <SelectionActionBar.Action label="Mark read" onClick={() => {}} />
              <SelectionActionBar.Action label="Mark unread" onClick={() => {}} />
              <SelectionActionBar.Divider />
              <Dropdown<string>
                value={null}
                placeholder="Categorize"
                options={[{ value: 'clients', label: 'Clients' }, { value: 'newsletters', label: 'Newsletters' }, { value: 'finance', label: 'Finance' }]}
                onChange={() => {}}
                size="sm"
              />
              <SelectionActionBar.Divider />
              <SelectionActionBar.Action label="Follow up" tone="amber" onClick={() => {}} />
              <SelectionActionBar.Action label="Remove follow-up" onClick={() => {}} />
              <SelectionActionBar.Divider />
              <SelectionActionBar.Action label="Move to trash" tone="danger" confirmRequired onClick={() => setSab4Visible(false)} />
            </SelectionActionBar>
            <div style={{ padding: '12px 16px', background: 'var(--evo-bg-2)', fontFamily: 'var(--evo-font)', fontSize: 12, color: 'var(--evo-fg-3)' }}>Content area</div>
          </div>
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'visible', type: 'boolean', required: true, description: 'Controls slide-in/out (max-height + opacity transition). Mount it always; set visible to toggle.' },
        { prop: 'countLabel', type: 'string', required: true, description: 'Pre-translated string shown on the left, e.g. t("leads.selected", { count: n }).' },
        { prop: 'onClearSelection', type: '() => void', required: true, description: 'Called by the × button on the right. Should clear the selection set.' },
        { prop: 'children', type: 'ReactNode', required: false, description: 'Actions area — any mix of SelectionActionBar.Action, SelectionActionBar.Divider, Dropdown, or inline spans.' },
        { prop: 'SelectionActionBar.Divider', type: 'component', required: false, description: 'Thin vertical separator (1px × 16px) between logical action groups (read state | categorize | followup | destructive).' },
        { prop: 'SelectionActionBar.Action tone', type: "'default' | 'primary' | 'danger' | 'danger-fill' | 'amber'", required: false, description: 'default=outline, primary=cyan-filled, danger=danger-outline, danger-fill=danger-filled, amber=amber-outline.' },
        { prop: 'SelectionActionBar.Action confirmRequired', type: 'boolean', required: false, description: 'When true, clicking the action enters confirm mode — bar shows "Are you sure?" + Cancel + the action label. No browser dialogs.' },
      ]} />

      {/* ── Tabs ── */}
      <Label>Tabs</Label>

      <Example variant="basic — labels only" code={`<Tabs
  value={tab}
  onChange={setTab}
  tabs={[
    { value: 'inbox',    label: 'Inbox' },
    { value: 'sent',     label: 'Sent' },
    { value: 'drafts',   label: 'Drafts' },
    { value: 'trash',    label: 'Trash' },
  ]}
/>`}>
        <Tabs<string>
          value={tabValue}
          onChange={setTabValue}
          tabs={[
            { value: 'inbox',  label: 'Inbox' },
            { value: 'sent',   label: 'Sent' },
            { value: 'drafts', label: 'Drafts' },
            { value: 'trash',  label: 'Trash' },
          ]}
        />
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          Active: <strong style={{ color: 'var(--evo-fg-2)' }}>{tabValue}</strong>
        </p>
      </Example>

      <Example variant="with counts and disabled" code={`<Tabs
  value={tab}
  onChange={setTab}
  tabs={[
    { value: 'inbox',  label: 'Inbox',  count: 12 },
    { value: 'sent',   label: 'Sent',   count: 348 },
    { value: 'drafts', label: 'Drafts', count: 3 },
    { value: 'trash',  label: 'Trash',  count: 0, disabled: true },
  ]}
/>`}>
        <Tabs<string>
          value={tabValue}
          onChange={setTabValue}
          tabs={[
            { value: 'inbox',  label: 'Inbox',  count: 12 },
            { value: 'sent',   label: 'Sent',   count: 348 },
            { value: 'drafts', label: 'Drafts', count: 3 },
            { value: 'trash',  label: 'Trash',  count: 0, disabled: true },
          ]}
        />
      </Example>

      <Example variant="with icons" code={`<Tabs
  value={tab}
  onChange={setTab}
  tabs={[
    { value: 'inbox', label: 'Inbox', icon: <Icon name="envelope" size={12} /> },
    { value: 'sent',  label: 'Sent',  icon: <Icon name="send" size={12} /> },
    { value: 'trash', label: 'Trash', icon: <Icon name="trash" size={12} /> },
  ]}
/>`}>
        <Tabs<string>
          value={tabValue}
          onChange={setTabValue}
          tabs={[
            { value: 'inbox', label: 'Inbox', icon: <Icon name="envelope" size={12} /> },
            { value: 'sent',  label: 'Sent',  icon: <Icon name="send" size={12} /> },
            { value: 'trash', label: 'Trash', icon: <Icon name="close" size={12} /> },
          ]}
        />
      </Example>

      <Example variant="sizes — sm / md" code={`<Tabs size="sm" ... />
<Tabs size="md" ... />`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            <Button variant={tabSize === 'sm' ? 'solid' : 'outline'} tone={tabSize === 'sm' ? 'cyan' : 'muted'} size="sm" onClick={() => setTabSize('sm')}>sm</Button>
            <Button variant={tabSize === 'md' ? 'solid' : 'outline'} tone={tabSize === 'md' ? 'cyan' : 'muted'} size="sm" onClick={() => setTabSize('md')}>md</Button>
          </div>
          <Tabs<string>
            size={tabSize}
            value={tabValue}
            onChange={setTabValue}
            tabs={[
              { value: 'inbox',  label: 'Inbox',  count: 12 },
              { value: 'sent',   label: 'Sent' },
              { value: 'drafts', label: 'Drafts', count: 3 },
            ]}
          />
        </div>
      </Example>

      <Example variant="fullWidth — distribuye tabs por igual" code={`<Tabs fullWidth value={tab} onChange={setTab} tabs={...} />`}>
        <Tabs<string>
          fullWidth
          value={tabFw}
          onChange={setTabFw}
          tabs={[
            { value: 'all',     label: 'Todos' },
            { value: 'pending', label: 'Pendientes', count: 5 },
            { value: 'done',    label: 'Listos' },
          ]}
        />
      </Example>

      <PropsTable rows={[
        { prop: 'tabs',      type: 'TabItem<V>[]',          required: true,  description: 'Lista de tabs. Cada uno: { value, label, count?, icon?, disabled? }.' },
        { prop: 'value',     type: 'V',                     required: true,  description: 'Tab actualmente activo.' },
        { prop: 'onChange',  type: '(value: V) => void',    required: true,  description: 'Llamado al hacer click en un tab.' },
        { prop: 'size',      type: "'sm' | 'md'",           required: false, description: 'Altura 32 / 40px. Default md.' },
        { prop: 'fullWidth', type: 'boolean',                required: false, description: 'Distribuye los tabs ocupando todo el ancho.' },
      ]} />
    </ShowcaseSection>
  );
}

// ── Primitives ────────────────────────────────────────────────────────────────

function PrimitivesSection() {
  const [btnLoading, setBtnLoading] = useState(false);
  const [tog1, setTog1]             = useState(false);
  const [tog2, setTog2]             = useState(true);
  const [tog3, setTog3]             = useState(false);
  const [radioV, setRadioV]         = useState('opt-a');
  const [radioH, setRadioH]         = useState('opt-2');
  const [pillRm, setPillRm]         = useState(true);
  const [pillClicks, setPillClicks] = useState(0);
  const [inpSm, setInpSm]           = useState('');
  const [inpMd, setInpMd]           = useState('');
  const [inpLg, setInpLg]           = useState('');
  const [inpSearch, setInpSearch]   = useState('');
  const [inpErr, setInpErr]         = useState('bad-value');
  const [taShort, setTaShort]       = useState('');
  const [taLong, setTaLong]         = useState('');
  const [selBasic, setSelBasic]     = useState<string | null>(null);
  const [selSearch, setSelSearch]   = useState<string | null>(null);
  const [selDesc, setSelDesc]       = useState<string | null>(null);
  const [selClr, setSelClr]         = useState<string | null>('opt-a');
  const [dateVal, setDateVal]       = useState<Date | null>(null);
  const [rangeVal, setRangeVal]     = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [dateMin, setDateMin]       = useState<Date | null>(null);
  const [rteValue, setRteValue]     = useState('');
  const [rteLimited, setRteLimited] = useState('');

  function triggerLoad() {
    setBtnLoading(true);
    setTimeout(() => setBtnLoading(false), 1800);
  }

  const BTN_VARIANTS  = ['solid', 'outline', 'ghost', 'link'] as const;
  const BTN_TONES     = ['cyan', 'amber', 'danger', 'muted', 'neutral'] as const;
  const PILL_TONES_M  = ['default', 'cyan', 'warn', 'amber', 'danger', 'success', 'info', 'muted', 'blue', 'steel', 'soft'] as const;
  const PILL_VARIANTS = ['solid', 'subtle', 'outline'] as const;

  return (
    <ShowcaseSection
      id="primitives"
      title="Primitives"
      description="Button · IconButton · Pill · Input · Textarea · Radio · Toggle — packages/web/src/components/ui/"
    >

      {/* ═════════════ BUTTON ═════════════ */}
      <Label>Button</Label>

      <Example
        variant="variant × tone matrix"
        code={`<Button variant="solid"   tone="cyan"    size="sm">cyan</Button>
<Button variant="outline" tone="amber"   size="sm">amber</Button>
<Button variant="ghost"   tone="danger"  size="sm">danger</Button>
<Button variant="link"    tone="muted"   size="sm">muted</Button>`}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--evo-font)', minWidth: 540 }}>
            <thead>
              <tr>
                <th style={HEAD_CELL}>↓ variant / tone →</th>
                {BTN_TONES.map(t => <th key={t} style={HEAD_CELL}>{t}</th>)}
              </tr>
            </thead>
            <tbody>
              {BTN_VARIANTS.map(v => (
                <tr key={v}>
                  <td style={{ ...CELL, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font-mono)', whiteSpace: 'nowrap' }}>{v}</td>
                  {BTN_TONES.map(t => (
                    <td key={t} style={CELL}>
                      <Button variant={v} tone={t} size="sm">{t}</Button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Example>

      <Example
        variant="sizes sm · md · lg"
        code={`<Button variant="solid" tone="cyan" size="sm">Small</Button>
<Button variant="solid" tone="cyan" size="md">Medium</Button>
<Button variant="solid" tone="cyan" size="lg">Large</Button>`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="solid" tone="cyan" size="sm">Small</Button>
          <Button variant="solid" tone="cyan" size="md">Medium</Button>
          <Button variant="solid" tone="cyan" size="lg">Large</Button>
        </div>
      </Example>

      <Example
        variant="states — disabled · loading"
        description="Click 'Trigger load' to see the spinner (1.8 s)"
        code={`<Button variant="solid"   tone="cyan"  disabled>Disabled</Button>
<Button variant="outline" tone="muted" disabled>Disabled outline</Button>
<Button variant="solid"   tone="cyan"  loading={loading} onClick={trigger}>…</Button>`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="solid" tone="cyan" disabled>Disabled</Button>
          <Button variant="outline" tone="muted" disabled>Disabled outline</Button>
          <Button variant="solid" tone="cyan" loading={btnLoading} onClick={triggerLoad}>
            {btnLoading ? 'Loading…' : 'Trigger load'}
          </Button>
        </div>
      </Example>

      <Example
        variant="icon before · after"
        code={`<Button variant="solid"   tone="cyan"   icon={<Icon name="send"        size={14} />}>Send</Button>
<Button variant="outline" tone="muted"  iconAfter={<Icon name="arrow-right" size={14} />}>Continue</Button>
<Button variant="ghost"   tone="danger" icon={<Icon name="flag"        size={14} />}>Flag</Button>
<Button variant="link"    tone="cyan"   iconAfter={<Icon name="chevron-down"  size={12} />}>More</Button>`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="solid"   tone="cyan"   size="md" icon={<Icon name="send"        size={14} />}>Send</Button>
          <Button variant="outline" tone="muted"  size="md" iconAfter={<Icon name="arrow-right" size={14} />}>Continue</Button>
          <Button variant="ghost"   tone="danger" size="md" icon={<Icon name="flag"        size={14} />}>Flag</Button>
          <Button variant="link"    tone="cyan"   size="sm" iconAfter={<Icon name="chevron-down"  size={12} />}>More</Button>
        </div>
      </Example>

      <Example
        variant="IconButton — icon-only square"
        code={`<IconButton icon={<Icon name="close"    size={14} />} ariaLabel="close"    variant="ghost"   tone="muted"   size="sm" />
<IconButton icon={<Icon name="settings" size={16} />} ariaLabel="settings" variant="outline" tone="neutral" size="md" />
<IconButton icon={<Icon name="send"     size={18} />} ariaLabel="send"     variant="solid"   tone="cyan"    size="lg" />`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <IconButton icon={<Icon name="close"    size={14} />} ariaLabel="ghost sm"    variant="ghost"   tone="muted"   size="sm" />
          <IconButton icon={<Icon name="settings" size={16} />} ariaLabel="outline md"  variant="outline" tone="neutral" size="md" />
          <IconButton icon={<Icon name="send"     size={18} />} ariaLabel="solid lg"    variant="solid"   tone="cyan"    size="lg" />
          <IconButton icon={<Icon name="flag"     size={16} />} ariaLabel="danger md"   variant="outline" tone="danger"  size="md" />
          <IconButton icon={<Icon name="compose"  size={16} />} ariaLabel="amber ghost" variant="ghost"   tone="amber"   size="md" />
          <IconButton icon={<Icon name="settings" size={16} />} ariaLabel="disabled"    variant="outline" tone="muted"   size="md" disabled />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'variant',   type: "'solid' | 'outline' | 'ghost' | 'link'",             required: true,  description: 'Visual style.' },
        { prop: 'tone',      type: "'cyan' | 'amber' | 'danger' | 'muted' | 'neutral'",  required: true,  description: 'Color set.' },
        { prop: 'size',      type: "'sm' | 'md' | 'lg'",                                  required: false, description: 'Height 28/32/40px, font 12/13/14px. Default md.' },
        { prop: 'icon',      type: 'ReactNode',                                            required: false, description: 'Icon before the label.' },
        { prop: 'iconAfter', type: 'ReactNode',                                            required: false, description: 'Icon after the label.' },
        { prop: 'loading',   type: 'boolean',                                              required: false, description: 'Shows spinner, disables click.' },
        { prop: 'disabled',  type: 'boolean',                                              required: false, description: 'Native disabled, 0.5 opacity.' },
        { prop: 'fullWidth', type: 'boolean',                                              required: false, description: 'width: 100%.' },
        { prop: 'onClick',   type: '(e: MouseEvent) => void',                             required: false, description: 'Click handler.' },
        { prop: 'type',      type: "'button' | 'submit'",                                  required: false, description: "Default 'button'." },
      ]} />

      {/* ═════════════ PILL ═════════════ */}
      <Label>Pill</Label>

      <Example
        variant="tone × variant matrix"
        code={`<Pill tone="cyan"   variant="solid">  cyan</Pill>
<Pill tone="cyan"   variant="subtle"> cyan</Pill>
<Pill tone="danger" variant="outline">danger</Pill>`}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--evo-font)' }}>
            <thead>
              <tr>
                <th style={HEAD_CELL}>tone</th>
                {PILL_VARIANTS.map(v => <th key={v} style={HEAD_CELL}>{v}</th>)}
              </tr>
            </thead>
            <tbody>
              {PILL_TONES_M.map(tone => (
                <tr key={tone}>
                  <td style={{ ...CELL, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font-mono)' }}>{tone}</td>
                  {PILL_VARIANTS.map(v => (
                    <td key={v} style={CELL}>
                      <Pill tone={tone} variant={v} size="sm">{tone}</Pill>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Example>

      <Example
        variant="sizes xs · sm · md"
        code={`<Pill size="xs" tone="cyan" variant="outline">xs</Pill>
<Pill size="sm" tone="cyan" variant="outline">sm</Pill>
<Pill size="md" tone="cyan" variant="outline">md</Pill>`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pill size="xs" tone="cyan" variant="outline">xs</Pill>
          <Pill size="sm" tone="cyan" variant="outline">sm</Pill>
          <Pill size="md" tone="cyan" variant="outline">md</Pill>
        </div>
      </Example>

      <Example
        variant="with icon · removable · clickable"
        code={`<Pill tone="cyan" variant="subtle" icon={<Icon name="person" size={10} />}>With icon</Pill>
{pillRm && <Pill tone="warn" variant="outline" onRemove={() => setPillRm(false)}>Removable (×)</Pill>}
<Pill tone="info" variant="subtle" onClick={() => setPillClicks(n => n + 1)}>Clicks: {n}</Pill>`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pill tone="cyan" variant="subtle" icon={<Icon name="person" size={10} />}>With icon</Pill>
          {pillRm
            ? <Pill tone="warn" variant="outline" onRemove={() => setPillRm(false)}>Removable (×)</Pill>
            : (
              <button
                onClick={() => setPillRm(true)}
                style={{ fontSize: 11, color: 'var(--evo-fg-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--evo-font)' }}
              >
                ↺ restore
              </button>
            )
          }
          <Pill tone="info" variant="subtle" onClick={() => setPillClicks(n => n + 1)}>
            Clicks: {pillClicks}
          </Pill>
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'tone',     type: "'default' | 'cyan' | 'warn' | 'amber' | 'danger' | 'success' | 'info' | 'muted' | 'blue' | 'steel' | 'soft'", required: false, description: "Color set. Default 'default'." },
        { prop: 'variant',  type: "'solid' | 'subtle' | 'outline'",                                                                                 required: false, description: "Fill style. Default 'outline'." },
        { prop: 'size',     type: "'xs' | 'sm' | 'md'",                                                                                             required: false, description: 'Font 10/11/12px. Default sm.' },
        { prop: 'icon',     type: 'ReactNode | IconName',                                                                                            required: false, description: 'Leading icon. ReactNode or legacy icon name string.' },
        { prop: 'onRemove', type: '() => void',                                                                                                      required: false, description: 'Renders × button at right edge.' },
        { prop: 'onClick',  type: '() => void',                                                                                                      required: false, description: 'Makes pill clickable with pointer cursor.' },
      ]} />

      {/* ═════════════ INPUT ═════════════ */}
      <Label>Input</Label>

      <Example
        variant="sizes sm · md · lg with label"
        code={`<Input size="sm" label="Label sm" placeholder="placeholder…" value={v} onChange={setV} />
<Input size="md" label="Label md" placeholder="placeholder…" value={v} onChange={setV} />
<Input size="lg" label="Label lg" placeholder="placeholder…" value={v} onChange={setV} />`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320 }}>
          <Input size="sm" label="Label sm" placeholder="placeholder…" value={inpSm} onChange={setInpSm} />
          <Input size="md" label="Label md" placeholder="placeholder…" value={inpMd} onChange={setInpMd} />
          <Input size="lg" label="Label lg" placeholder="placeholder…" value={inpLg} onChange={setInpLg} />
        </div>
      </Example>

      <Example
        variant="with iconBefore · error state · disabled"
        code={`<Input iconBefore={<Icon name="search" size={14} />} placeholder="Search…" value={v} onChange={setV} />
<Input value="bad-value" error="Must be a valid email" onChange={setV} />
<Input value="disabled input" disabled onChange={() => {}} />`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320 }}>
          <Input iconBefore={<Icon name="search" size={14} />} placeholder="Search…" value={inpSearch} onChange={setInpSearch} />
          <Input value={inpErr} onChange={setInpErr} error="Must be a valid email" />
          <Input value="disabled input" disabled onChange={() => {}} />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'value',                type: 'string',                         required: true,  description: 'Controlled value.' },
        { prop: 'onChange',             type: '(value: string) => void',        required: true,  description: 'Called on every keystroke.' },
        { prop: 'size',                 type: "'sm' | 'md' | 'lg'",             required: false, description: 'Height 28/32/40px. Default md.' },
        { prop: 'label',                type: 'string',                         required: false, description: 'Label above the field.' },
        { prop: 'helperText',           type: 'string',                         required: false, description: 'Muted hint below the field.' },
        { prop: 'iconBefore/iconAfter', type: 'ReactNode',                      required: false, description: 'Icons inside left / right edge.' },
        { prop: 'error',                type: 'boolean | string',               required: false, description: 'true = red border; string = border + error message.' },
        { prop: 'fullWidth',            type: 'boolean',                        required: false, description: 'width: 100%.' },
        { prop: 'disabled',             type: 'boolean',                        required: false, description: '0.5 opacity, cursor not-allowed.' },
      ]} />

      {/* ═════════════ TEXTAREA ═════════════ */}
      <Label>Textarea</Label>

      <Example
        variant="rows 3 · rows 5 · error state"
        code={`<Textarea rows={3} label="Short note" placeholder="…" value={v} onChange={setV} fullWidth />
<Textarea rows={5} label="Long note"  placeholder="…" value={v} onChange={setV} fullWidth />
<Textarea rows={3} label="Error"      error="Required"  value="" onChange={() => {}} fullWidth />`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 360 }}>
          <Textarea rows={3} label="Short note (rows 3)" placeholder="Write a short note…"        value={taShort} onChange={setTaShort} fullWidth />
          <Textarea rows={5} label="Long note (rows 5)"  placeholder="Write a longer description…" value={taLong}  onChange={setTaLong}  fullWidth />
          <Textarea rows={3} label="Error state"         error="This field is required"            value=""        onChange={() => {}}   fullWidth />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'value',     type: 'string',                        required: true,  description: 'Controlled value.' },
        { prop: 'onChange',  type: '(value: string) => void',       required: true,  description: 'Called on every keystroke.' },
        { prop: 'label',     type: 'string',                        required: false, description: 'Label above the field.' },
        { prop: 'error',     type: 'boolean | string',              required: false, description: 'true = red border; string = border + error message.' },
        { prop: 'resize',    type: "'none' | 'vertical' | 'both'",  required: false, description: "CSS resize. Default 'vertical'." },
        { prop: 'fullWidth', type: 'boolean',                       required: false, description: 'width: 100%.' },
        { prop: 'maxLength', type: 'number',                        required: false, description: 'Native maxlength attribute.' },
      ]} />

      {/* ═════════════ RADIO ═════════════ */}
      <Label>Radio · RadioGroup</Label>

      <Example
        variant="vertical group (default)"
        code={`<RadioGroup
  value={radio}
  onChange={setRadio}
  options={[
    { value: 'a', label: 'Option A' },
    { value: 'b', label: 'Option B' },
    { value: 'c', label: 'Option C (disabled)', disabled: true },
  ]}
/>`}
      >
        <RadioGroup
          value={radioV}
          onChange={setRadioV}
          options={[
            { value: 'opt-a', label: 'Option A' },
            { value: 'opt-b', label: 'Option B' },
            { value: 'opt-c', label: 'Option C (disabled)', disabled: true },
          ]}
        />
      </Example>

      <Example
        variant="horizontal group"
        code={`<RadioGroup
  direction="horizontal"
  value={radio}
  onChange={setRadio}
  options={[
    { value: '1', label: 'Option 1' },
    { value: '2', label: 'Option 2' },
    { value: '3', label: 'Option 3' },
  ]}
/>`}
      >
        <RadioGroup
          direction="horizontal"
          value={radioH}
          onChange={setRadioH}
          options={[
            { value: 'opt-1', label: 'Option 1' },
            { value: 'opt-2', label: 'Option 2' },
            { value: 'opt-3', label: 'Option 3' },
          ]}
        />
      </Example>

      <PropsTable rows={[
        { prop: 'value (Radio)',          type: 'string',                              required: true,  description: 'Value this radio represents.' },
        { prop: 'checked (Radio)',         type: 'boolean',                             required: true,  description: 'Whether selected.' },
        { prop: 'onChange (Radio)',        type: '() => void',                          required: true,  description: 'Called when clicked.' },
        { prop: 'label (Radio)',           type: 'string',                              required: true,  description: 'Label text.' },
        { prop: 'disabled (Radio)',        type: 'boolean',                             required: false, description: '0.5 opacity, not-allowed cursor.' },
        { prop: 'options (RadioGroup)',    type: 'Array<{ value, label, disabled? }>', required: true,  description: 'Option list.' },
        { prop: 'direction (RadioGroup)', type: "'vertical' | 'horizontal'",           required: false, description: "Layout direction. Default 'vertical'." },
      ]} />

      {/* ═════════════ TOGGLE ═════════════ */}
      <Label>Toggle</Label>

      <Example
        variant="sm · md — on/off — with description — disabled"
        code={`<Toggle size="sm" checked={v} onChange={setV} label="Notifications (sm)" />
<Toggle size="md" checked={v} onChange={setV} label="Dark mode" description="Apply to the whole app" />
<Toggle size="md" checked disabled label="Forced on" description="Cannot be changed" />`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 360 }}>
          <Toggle size="sm" checked={tog1} onChange={setTog1} label={`Notifications sm — ${tog1 ? 'On' : 'Off'}`} />
          <Toggle size="md" checked={tog2} onChange={setTog2} label={`Dark mode md — ${tog2 ? 'On' : 'Off'}`} description="Apply to the whole app" />
          <Toggle size="sm" checked={tog3} onChange={setTog3} label="With description (sm)" description="Tap to enable this feature" />
          <Toggle size="md" checked onChange={() => {}} disabled label="Forced on (disabled)" description="Cannot be changed" />
          <Toggle size="md" checked={false} onChange={() => {}} disabled label="Forced off (disabled)" />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'checked',     type: 'boolean',                      required: true,  description: 'On/off state.' },
        { prop: 'onChange',    type: '(checked: boolean) => void',   required: true,  description: 'Called with new value on click.' },
        { prop: 'size',        type: "'sm' | 'md'",                  required: false, description: 'Track 36×20 (sm) or 44×24 (md). Default md.' },
        { prop: 'label',       type: 'string',                       required: false, description: 'Label to the right of the toggle.' },
        { prop: 'description', type: 'string',                       required: false, description: 'Secondary muted text below the label.' },
        { prop: 'disabled',    type: 'boolean',                      required: false, description: '0.5 opacity, click disabled.' },
      ]} />

      {/* ═════════════ SELECT ═════════════ */}
      <Label>Select</Label>

      <Example variant="básico — single select" code={`<Select
  value={val}
  onChange={setVal}
  options={[
    { value: 'inbox',  label: 'Inbox' },
    { value: 'leads',  label: 'Leads' },
    { value: 'closed', label: 'Closed' },
  ]}
  placeholder="Choose stage…"
/>`}>
        <Select<string>
          value={selBasic}
          onChange={setSelBasic}
          placeholder="Choose stage…"
          options={[
            { value: 'inbox',  label: 'Inbox' },
            { value: 'leads',  label: 'Leads' },
            { value: 'active', label: 'Active' },
            { value: 'closed', label: 'Closed' },
          ]}
        />
        {selBasic && <p style={{ marginTop: 8, fontSize: 12, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>Selected: <strong style={{ color: 'var(--evo-fg-2)' }}>{selBasic}</strong></p>}
      </Example>

      <Example variant="searchable — filtra al escribir" code={`<Select searchable value={val} onChange={setVal} options={countries} />`}>
        <Select<string>
          searchable
          value={selSearch}
          onChange={setSelSearch}
          placeholder="Search country…"
          label="Country"
          options={[
            { value: 'ar', label: 'Argentina' },
            { value: 'br', label: 'Brazil' },
            { value: 'cl', label: 'Chile' },
            { value: 'co', label: 'Colombia' },
            { value: 'mx', label: 'Mexico' },
            { value: 'pe', label: 'Peru' },
            { value: 'uy', label: 'Uruguay' },
            { value: 'us', label: 'United States' },
            { value: 'es', label: 'Spain' },
          ]}
        />
      </Example>

      <Example variant="options con description" code={`<Select options={[{ value: 'pro', label: 'Pro', description: 'Unlimited seats' }]} />`}>
        <Select<string>
          value={selDesc}
          onChange={setSelDesc}
          placeholder="Choose plan…"
          label="Plan"
          helperText="Each plan has different limits"
          options={[
            { value: 'free',  label: 'Free',       description: 'Up to 100 contacts, 1 inbox' },
            { value: 'pro',   label: 'Pro',         description: 'Unlimited contacts, 5 inboxes' },
            { value: 'team',  label: 'Team',        description: 'Everything in Pro + collaboration' },
            { value: 'ent',   label: 'Enterprise',  description: 'Custom limits, SLA, dedicated support', disabled: true },
          ]}
        />
      </Example>

      <Example variant="clearable + error state + disabled" code={`<Select clearable error="Required field" disabled ... />`}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Select<string>
            clearable
            value={selClr}
            onChange={setSelClr}
            label="Clearable"
            options={[{ value: 'opt-a', label: 'Option A' }, { value: 'opt-b', label: 'Option B' }]}
          />
          <Select<string>
            value={null}
            onChange={() => {}}
            label="Error state"
            error="Este campo es obligatorio"
            options={[{ value: 'x', label: 'Option X' }]}
          />
          <Select<string>
            disabled
            value="opt-a"
            onChange={() => {}}
            label="Disabled"
            options={[{ value: 'opt-a', label: 'Option A' }]}
          />
        </div>
      </Example>

      <Example variant="sizes — sm / md / lg" code={`<Select size="sm" ... />\n<Select size="md" ... />\n<Select size="lg" ... />`}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          {(['sm', 'md', 'lg'] as const).map(sz => (
            <Select<string>
              key={sz}
              size={sz}
              value={null}
              onChange={() => {}}
              placeholder={sz}
              options={[{ value: 'x', label: 'Option' }]}
            />
          ))}
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'value',       type: 'T | null',                                required: true,  description: 'Valor seleccionado actualmente. null = sin selección.' },
        { prop: 'onChange',    type: '(value: T | null) => void',                required: true,  description: 'Llamado con el nuevo valor al seleccionar, o null si clearable.' },
        { prop: 'options',     type: 'SelectOption<T>[]',                        required: true,  description: 'Array de { value, label, disabled?, description? }.' },
        { prop: 'placeholder', type: 'string',                                   required: false, description: 'Texto cuando no hay selección. Default "Select…".' },
        { prop: 'size',        type: "'sm' | 'md' | 'lg'",                       required: false, description: 'Alto 28 / 32 / 40px. Default md.' },
        { prop: 'label',       type: 'string',                                   required: false, description: 'Label encima del trigger.' },
        { prop: 'helperText',  type: 'string',                                   required: false, description: 'Texto de ayuda debajo.' },
        { prop: 'error',       type: 'boolean | string',                         required: false, description: 'Si string, muestra el mensaje de error. Si true, borde rojo.' },
        { prop: 'searchable',  type: 'boolean',                                  required: false, description: 'Input de búsqueda en el top del menú. Default false.' },
        { prop: 'clearable',   type: 'boolean',                                  required: false, description: 'Muestra × para limpiar el valor. Default false.' },
        { prop: 'disabled',    type: 'boolean',                                  required: false, description: 'Deshabilita interacción. Default false.' },
        { prop: 'fullWidth',   type: 'boolean',                                  required: false, description: 'Ocupa 100% del contenedor. Default false.' },
      ]} />

      {/* ═════════════ DATE PICKER ═════════════ */}
      <Label>DatePicker</Label>

      <Example variant="single — selecciona un día" code={`<DatePicker mode="single" value={date} onChange={setDate} label="Fecha" />`}>
        <DatePicker
          mode="single"
          value={dateVal}
          onChange={setDateVal}
          label="Fecha de seguimiento"
          helperText="Seleccioná el día de la reunión"
        />
        {dateVal && <p style={{ marginTop: 8, fontSize: 12, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>Seleccionado: <strong style={{ color: 'var(--evo-fg-2)' }}>{dateVal.toLocaleDateString('es')}</strong></p>}
      </Example>

      <Example variant="range — desde / hasta" code={`<DatePicker mode="range" value={range} onChange={setRange} />`}>
        <DatePicker
          mode="range"
          value={rangeVal}
          onChange={setRangeVal}
          label="Período"
          helperText="Primer click = inicio · Segundo click = fin"
        />
        {(rangeVal.from ?? rangeVal.to) && (
          <p style={{ marginTop: 8, fontSize: 12, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
            Desde <strong style={{ color: 'var(--evo-fg-2)' }}>{rangeVal.from?.toLocaleDateString('es') ?? '—'}</strong>{' '}
            hasta <strong style={{ color: 'var(--evo-fg-2)' }}>{rangeVal.to?.toLocaleDateString('es') ?? '—'}</strong>
          </p>
        )}
      </Example>

      <Example variant="minDate / maxDate — rango permitido" code={`<DatePicker mode="single" minDate={new Date()} value={date} onChange={setDate} />`}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <DatePicker
            mode="single"
            value={dateMin}
            onChange={setDateMin}
            label="Solo fechas futuras"
            helperText="minDate = hoy"
            minDate={new Date()}
          />
          <DatePicker
            mode="single"
            value={null}
            onChange={() => {}}
            label="Disabled"
            disabled
          />
        </div>
      </Example>

      <Example variant="error state + sizes" code={`<DatePicker mode="single" error="Fecha inválida" size="sm" ... />`}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <DatePicker
            mode="single"
            value={null}
            onChange={() => {}}
            label="Error"
            error="La fecha es obligatoria"
          />
          {(['sm', 'md', 'lg'] as const).map(sz => (
            <DatePicker
              key={sz}
              mode="single"
              value={null}
              onChange={() => {}}
              size={sz}
              placeholder={`size ${sz}`}
            />
          ))}
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'mode',        type: "'single' | 'range'",                        required: true,  description: 'Modo de selección. Determina el tipo de value y onChange.' },
        { prop: 'value',       type: 'Date | null (single) · {from,to} (range)',  required: true,  description: 'Valor controlado.' },
        { prop: 'onChange',    type: 'callback equivalente al value',              required: true,  description: 'Llamado al seleccionar día o limpiar.' },
        { prop: 'placeholder', type: 'string',                                    required: false, description: 'Texto cuando no hay valor. Default "Pick a date" / "Pick a range".' },
        { prop: 'size',        type: "'sm' | 'md' | 'lg'",                        required: false, description: 'Alto del trigger 28 / 32 / 40px. Default md.' },
        { prop: 'label',       type: 'string',                                    required: false, description: 'Label encima del trigger.' },
        { prop: 'helperText',  type: 'string',                                    required: false, description: 'Texto de ayuda debajo.' },
        { prop: 'error',       type: 'boolean | string',                          required: false, description: 'Borde rojo + mensaje de error.' },
        { prop: 'minDate',     type: 'Date',                                      required: false, description: 'Días anteriores deshabilitados (opacity 0.3, no clickables).' },
        { prop: 'maxDate',     type: 'Date',                                      required: false, description: 'Días posteriores deshabilitados.' },
        { prop: 'disabled',    type: 'boolean',                                   required: false, description: 'Deshabilita el trigger. Default false.' },
        { prop: 'fullWidth',   type: 'boolean',                                   required: false, description: 'Ocupa 100% del contenedor. Default false.' },
        { prop: 'format',      type: '(d: Date) => string',                       required: false, description: 'Formatter personalizado. Default yyyy-mm-dd.' },
      ]} />

      {/* ═════════════ RICH TEXT EDITOR ═════════════ */}
      <Label>RichTextEditor</Label>

      <Example
        variant="default toolbar (full set)"
        code={`<RichTextEditor value={html} onChange={setHtml} placeholder="Write something…" />`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: 240 }}>
          <RichTextEditor
            value={rteValue}
            onChange={setRteValue}
            placeholder="Write something…"
          />
        </div>
      </Example>

      <Example
        variant="reduced toolbar (notes)"
        code={`<RichTextEditor\n  value={html}\n  onChange={setHtml}\n  toolbar={['bold','italic','underline','divider','link','divider','bulletList','orderedList']}\n  minHeight={100}\n/>`}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <RichTextEditor
            value={rteLimited}
            onChange={setRteLimited}
            toolbar={['bold', 'italic', 'underline', 'divider', 'link', 'divider', 'bulletList', 'orderedList']}
            minHeight={100}
            placeholder="Notes…"
          />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'value',           type: 'string',                          required: true,  description: 'HTML content (controlled).' },
        { prop: 'onChange',        type: '(html: string) => void',          required: true,  description: 'Fired on every edit.' },
        { prop: 'placeholder',     type: 'string',                          required: false, description: 'Placeholder text shown when editor is empty.' },
        { prop: 'minHeight',       type: 'number',                          required: false, description: 'Min height of the content area in px. Default 160.' },
        { prop: 'toolbar',         type: 'RichTextEditorToolbarItem[]',     required: false, description: 'Toolbar items. Default: full set with bold/italic/underline/strike/link/lists/blockquote/code.' },
        { prop: 'enableHeadings',  type: 'boolean',                         required: false, description: 'Enable TipTap heading marks. Default false.' },
        { prop: 'enableCodeBlock', type: 'boolean',                         required: false, description: 'Enable TipTap code block. Default false.' },
        { prop: 'ref',             type: 'RichTextEditorHandle',            required: false, description: 'Imperative handle: focus() and getText().' },
      ]} />

    </ShowcaseSection>
  );
}

// ── Data Display ─────────────────────────────────────────────────────────────

// ── Overlays ──────────────────────────────────────────────────────────────────

function OverlaysSection() {
  const [modalOpen, setModalOpen]       = useState(false);
  const [modalSize, setModalSize]       = useState<ModalSize>('md');
  const [modalVariant, setModalVariant] = useState<'basic' | 'footer' | 'prevent'>('basic');
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [drawerSide, setDrawerSide]     = useState<DrawerSide>('right');
  const [drawerSize, setDrawerSize]     = useState<DrawerSize>('md');
  const [confirmResult, setConfirmResult] = useState('');

  const toast = useToast();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const openModal = (size: ModalSize, variant: 'basic' | 'footer' | 'prevent') => {
    setModalSize(size); setModalVariant(variant); setModalOpen(true);
  };

  const ROW: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' };

  return (
    <ShowcaseSection id="overlays" title="Overlays"
      description="Modal, Drawer, Toast y ConfirmDialog — todos usan createPortal a document.body."
    >
      {/* ── Modal ── */}
      <Label>Modal — tamaños</Label>
      <Example variant="sm / md / lg / xl" code={`<Modal open={open} onClose={close} title="Título" size="md">\n  {children}\n</Modal>`}>
        <div style={ROW}>
          {(['sm', 'md', 'lg', 'xl'] as ModalSize[]).map(s => (
            <Button key={s} variant="outline" tone="muted" size="sm" onClick={() => openModal(s, 'basic')}>
              Open {s}
            </Button>
          ))}
        </div>
      </Example>

      <Example variant="con footer" code={`<Modal footer={<><Button>Cancel</Button><Button tone="cyan">Save</Button></>}>`}>
        <div style={ROW}>
          <Button variant="outline" tone="muted" size="sm" onClick={() => openModal('md', 'footer')}>Open con footer</Button>
        </div>
      </Example>

      <Example variant="preventClose" code={`<Modal preventClose={busy}> — X y escape bloqueados durante operación`}>
        <div style={ROW}>
          <Button variant="outline" tone="muted" size="sm" onClick={() => openModal('sm', 'prevent')}>Open preventClose</Button>
        </div>
      </Example>

      {/* Modal instances */}
      <Modal
        open={modalOpen && modalVariant === 'basic'}
        onClose={() => setModalOpen(false)}
        title="Ejemplo de Modal"
        description="Este es el texto descriptivo opcional del modal."
        size={modalSize}
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--evo-fg-2)', lineHeight: 1.6 }}>
          Contenido del modal. Tab y Shift+Tab quedan atrapados aquí. Escape cierra. Click en backdrop cierra.
        </p>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--evo-fg-3)' }}>
          Size actual: <strong>{modalSize}</strong>
        </p>
      </Modal>

      <Modal
        open={modalOpen && modalVariant === 'footer'}
        onClose={() => setModalOpen(false)}
        title="Modal con footer"
        description="Acciones en el footer alineadas a la derecha."
        size="md"
        footer={
          <>
            <Button variant="outline" tone="muted" size="sm" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="solid" tone="cyan" size="sm" onClick={() => setModalOpen(false)}>Guardar</Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--evo-fg-2)' }}>
          El footer acepta cualquier ReactNode alineado a la derecha.
        </p>
      </Modal>

      <Modal
        open={modalOpen && modalVariant === 'prevent'}
        onClose={() => setModalOpen(false)}
        title="preventClose activo"
        description="El X y Escape están bloqueados. Solo el botón interno cierra."
        size="sm"
        preventClose
        footer={
          <Button variant="solid" tone="danger" size="sm" onClick={() => setModalOpen(false)}>
            Cerrar (único método)
          </Button>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--evo-fg-2)' }}>
          Útil durante operaciones en progreso donde el usuario no debe poder escapar.
        </p>
      </Modal>

      {/* ── Drawer ── */}
      <Label>Drawer</Label>
      <Example variant="side + size" code={`<Drawer open={open} onClose={close} side="right" size="md" title="Panel">`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={ROW}>
            <span style={{ fontSize: 12, color: 'var(--evo-fg-3)', minWidth: 36 }}>Side:</span>
            {(['right', 'left', 'bottom'] as DrawerSide[]).map(s => (
              <Button key={s} variant={drawerSide === s ? 'solid' : 'outline'} tone={drawerSide === s ? 'cyan' : 'muted'} size="sm"
                onClick={() => setDrawerSide(s)}>{s}</Button>
            ))}
          </div>
          <div style={ROW}>
            <span style={{ fontSize: 12, color: 'var(--evo-fg-3)', minWidth: 36 }}>Size:</span>
            {(['sm', 'md', 'lg'] as DrawerSize[]).map(s => (
              <Button key={s} variant={drawerSize === s ? 'solid' : 'outline'} tone={drawerSize === s ? 'cyan' : 'muted'} size="sm"
                onClick={() => setDrawerSize(s)}>{s}</Button>
            ))}
          </div>
          <div>
            <Button variant="solid" tone="cyan" size="sm" onClick={() => setDrawerOpen(true)}>
              Open Drawer ({drawerSide} / {drawerSize})
            </Button>
          </div>
        </div>
      </Example>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} side={drawerSide} size={drawerSize} title="Panel lateral">
        <p style={{ margin: 0, fontSize: 13, color: 'var(--evo-fg-2)', lineHeight: 1.6 }}>
          Contenido del Drawer. Side: <strong>{drawerSide}</strong>, size: <strong>{drawerSize}</strong>.
        </p>
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--evo-fg-3)' }}>
          Escape y click en backdrop cierran. Focus trap activo.
        </p>
        <div style={{ marginTop: 16 }}>
          <Button variant="outline" tone="muted" size="sm" onClick={() => setDrawerOpen(false)}>Cerrar</Button>
        </div>
      </Drawer>

      {/* ── Toast ── */}
      <Label>Toast</Label>
      <Example variant="tones" code={`const toast = useToast();\ntoast.success('Saved');\ntoast.error('Failed', { description: err.message });\ntoast.info('Processing...');\ntoast.warning('Watch out');`}>
        <div style={ROW}>
          <Button variant="solid" tone="cyan" size="sm"
            onClick={() => toast.success('Guardado correctamente', { description: 'El archivo fue procesado.' })}>
            success
          </Button>
          <Button variant="solid" tone="danger" size="sm"
            onClick={() => toast.error('Error al guardar', { description: 'Connection timeout.' })}>
            error
          </Button>
          <Button variant="outline" tone="muted" size="sm"
            onClick={() => toast.info('Procesando en segundo plano...')}>
            info
          </Button>
          <Button variant="solid" tone="amber" size="sm"
            onClick={() => toast.warning('Cambios sin guardar')}>
            warning
          </Button>
        </div>
      </Example>

      <Example variant="con action + persistente" code={`toast.show({ message: '...', tone: 'success', action: { label: 'Deshacer', onClick: fn } });\ntoast.info('...', { duration: 0 }); // no auto-dismiss`}>
        <div style={ROW}>
          <Button variant="outline" tone="muted" size="sm"
            onClick={() => toast.show({ message: 'Email enviado', tone: 'success', action: { label: 'Deshacer', onClick: () => toast.info('Deshecho') } })}>
            Con action
          </Button>
          <Button variant="outline" tone="muted" size="sm"
            onClick={() => toast.info('Toast persistente — click × para cerrar', { duration: 0 })}>
            Persistente
          </Button>
          <Button variant="outline" tone="muted" size="sm"
            onClick={() => { toast.success('Uno'); toast.info('Dos'); toast.warning('Tres'); }}>
            Stack ×3
          </Button>
        </div>
      </Example>

      {/* ── ConfirmDialog ── */}
      <Label>ConfirmDialog</Label>
      <Example variant="danger / warning / neutral" code={`const { confirm, dialog } = useConfirm();\nconst ok = await confirm({ title: '...', tone: 'danger' });\nif (ok) doDelete();`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={ROW}>
            <Button variant="solid" tone="danger" size="sm" onClick={() => { void confirmAction({ title: '¿Eliminar ítem?', description: 'Esta acción no se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' }).then(ok => setConfirmResult(ok ? 'Confirmed ✓' : 'Cancelled ✗')); }}>Danger</Button>
            <Button variant="solid" tone="amber" size="sm" onClick={() => { void confirmAction({ title: '¿Continuar?', description: 'Esto modificará datos existentes.', tone: 'warning', confirmLabel: 'Continuar' }).then(ok => setConfirmResult(ok ? 'Confirmed ✓' : 'Cancelled ✗')); }}>Warning</Button>
            <Button variant="solid" tone="cyan" size="sm" onClick={() => { void confirmAction({ title: 'Aplicar cambios', description: 'Se guardará la nueva configuración.', tone: 'cyan' }).then(ok => setConfirmResult(ok ? 'Confirmed ✓' : 'Cancelled ✗')); }}>Neutral</Button>
            {confirmResult && (
              <span style={{ fontSize: 12, color: confirmResult.includes('✓') ? 'var(--evo-success)' : 'var(--evo-danger)' }}>
                Resultado: {confirmResult}
              </span>
            )}
          </div>
        </div>
      </Example>
      {confirmDialog}
    </ShowcaseSection>
  );
}

function DataDisplaySection() {
  const [selE, setSelE] = useState(false);

  const followupBadge = (
    <span key="fu" style={{ fontSize: '9px', padding: '0 8px', height: '18px', borderRadius: '100px', border: '0.5px solid #f59e0b', color: '#f59e0b', display: 'inline-flex', alignItems: 'center' }}>
      Followup
    </span>
  );
  const categoryBadge = (
    <span key="cat" style={{ fontSize: '9px', padding: '0 8px', height: '18px', borderRadius: '100px', border: '0.5px solid var(--evo-cyan)', color: 'var(--evo-cyan)', display: 'inline-flex', alignItems: 'center' }}>
      Clientes
    </span>
  );

  return (
    <ShowcaseSection
      id="data-display"
      title="Data Display"
      description="Badge · Avatar · ListItem · KpiPanel — packages/web/src/components/ui/"
    >
      {/* ── Badge ── */}
      <Label>Badge</Label>

      <Example variant="variant × tone matrix" code={`<Badge variant="outline" tone="cyan">Cyan</Badge>
<Badge variant="soft"    tone="amber">Amber</Badge>
<Badge variant="solid"   tone="danger">Danger</Badge>`}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--evo-font)' }}>
            <thead>
              <tr>
                <th style={HEAD_CELL}>tone</th>
                {(['solid', 'outline', 'soft'] as const).map(v => <th key={v} style={HEAD_CELL}>{v}</th>)}
              </tr>
            </thead>
            <tbody>
              {(['cyan', 'amber', 'danger', 'success', 'muted', 'neutral'] as const).map(tone => (
                <tr key={tone}>
                  <td style={{ ...CELL, color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font-mono)' }}>{tone}</td>
                  {(['solid', 'outline', 'soft'] as const).map(variant => (
                    <td key={variant} style={CELL}>
                      <Badge variant={variant} tone={tone}>{tone}</Badge>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Example>

      <Example variant="sizes — sm · md" code={`<Badge size="sm" tone="cyan">sm</Badge>
<Badge size="md" tone="cyan">md</Badge>`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge size="sm" tone="cyan" variant="outline">sm outline</Badge>
          <Badge size="md" tone="cyan" variant="outline">md outline</Badge>
          <Badge size="sm" tone="amber" variant="soft">sm soft</Badge>
          <Badge size="md" tone="amber" variant="soft">md soft</Badge>
        </div>
      </Example>

      <Example variant="with icons · clickable" code={`<Badge tone="cyan" leftIcon={<Icon name="ok" size={9} />}>Verified</Badge>
<Badge tone="danger" rightIcon={<Icon name="close" size={9} />} onClick={remove}>Tag</Badge>`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Badge tone="success" variant="soft" leftIcon={<Icon name="ok" size={9} />}>Verified</Badge>
          <Badge tone="amber" variant="outline" leftIcon={<Icon name="flag" size={9} />}>Followup</Badge>
          <Badge tone="cyan" variant="outline" onClick={() => {}}>Click me</Badge>
          <Badge tone="danger" variant="soft" rightIcon={<Icon name="close" size={9} />} onClick={() => {}}>Removable</Badge>
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'children',  type: 'ReactNode',                                                                       required: true,  description: 'Texto del badge.' },
        { prop: 'variant',   type: "'solid' | 'outline' | 'soft'",                                                    required: false, description: "Estilo visual. Default 'outline'." },
        { prop: 'tone',      type: "'cyan' | 'amber' | 'danger' | 'success' | 'muted' | 'neutral'",                   required: false, description: "Set de colores. Default 'neutral'." },
        { prop: 'size',      type: "'sm' | 'md'",                                                                     required: false, description: 'Alto 18 / 22px, font 9 / 11px. Default sm.' },
        { prop: 'leftIcon',  type: 'ReactNode',                                                                        required: false, description: 'Icono antes del texto.' },
        { prop: 'rightIcon', type: 'ReactNode',                                                                        required: false, description: 'Icono después del texto.' },
        { prop: 'onClick',   type: '(e: MouseEvent) => void',                                                          required: false, description: 'Si está, hace el badge clickable (cursor pointer + hover).' },
        { prop: 'title',     type: 'string',                                                                           required: false, description: 'Native title attribute para tooltip.' },
      ]} />

      {/* ── Avatar ── */}
      <Label>Avatar</Label>

      <Example variant="sizes — xs / sm / md / lg" code={`<Avatar name="Eduardo Carrasco" size="xs" />
<Avatar name="Eduardo Carrasco" size="sm" />
<Avatar name="Eduardo Carrasco" size="md" />
<Avatar name="Eduardo Carrasco" size="lg" />`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name="Eduardo Carrasco" size="xs" />
          <Avatar name="Eduardo Carrasco" size="sm" />
          <Avatar name="Eduardo Carrasco" size="md" />
          <Avatar name="Eduardo Carrasco" size="lg" />
        </div>
      </Example>

      <Example variant="deterministic palette — same name = same colour" code={`<Avatar name="Maria Lopez" />
<Avatar name="Carlos Ruiz" />
<Avatar name="Ana Garcia" />`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {['Eduardo Carrasco', 'Maria Lopez', 'Carlos Ruiz', 'Ana Garcia', 'Juan Perez', 'Sofia Diaz', 'Diego Martinez', 'Laura Sanchez'].map(n => (
            <div key={n} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <Avatar name={n} size="md" />
              <span style={{ fontSize: 10, color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>{n.split(' ')[0]}</span>
            </div>
          ))}
        </div>
      </Example>

      <Example variant="with status indicator" code={`<Avatar name="Eduardo" status="online" />
<Avatar name="Maria"   status="offline" />
<Avatar name="Carlos"  status="busy" />`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name="Eduardo Carrasco" size="md" status="online" />
          <Avatar name="Maria Lopez" size="md" status="offline" />
          <Avatar name="Carlos Ruiz" size="md" status="busy" />
          <Avatar name="Ana Garcia" size="lg" status="online" />
        </div>
      </Example>

      <Example variant="with image — fallback on error" code={`<Avatar name="User" src="https://..." />
<Avatar name="Broken" src="https://broken.url" />  {/* falls back to initials */}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name="Real Image" src="https://i.pravatar.cc/100?img=12" size="md" />
          <Avatar name="Broken Url" src="https://this-will-404-immediately.example.invalid/img.png" size="md" />
          <Avatar name="No Source" size="md" />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'name',    type: 'string',                                  required: true,  description: 'Usado para iniciales fallback y para hash de color determinístico.' },
        { prop: 'src',     type: 'string',                                   required: false, description: 'URL de imagen. Si falla, cae a iniciales.' },
        { prop: 'size',    type: "'xs' | 'sm' | 'md' | 'lg'",                required: false, description: '20 / 28 / 36 / 48px. Default md.' },
        { prop: 'status',  type: "'online' | 'offline' | 'busy'",            required: false, description: 'Punto indicador en la esquina inferior derecha.' },
        { prop: 'onClick', type: '() => void',                                required: false, description: 'Si está, agrega cursor pointer.' },
        { prop: 'title',   type: 'string',                                    required: false, description: 'Native title attribute. Default = name.' },
      ]} />

      {/* ── KpiPanel ── */}
      <Label>KpiPanel</Label>
      <p style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-fg-3)', margin: '0 0 16px' }}>
        KpiPanel is a <strong style={{ color: 'var(--evo-fg-2)' }}>composed Dashboard section</strong>, not a generic card primitive.
        It renders a fixed 3-column grid: icon | large number + label | heading + rows list.
        For a simple KPI chip, use a Pill or inline span instead.
      </p>

      <Example
        variant="Dashboard KpiPanel"
        fullWidth
        code={`import { KpiPanel } from 'components/ui/KpiPanel.js';\n\n<KpiPanel\n  icon="envelope"\n  num="12"\n  numColor="var(--evo-cyan)"\n  label={"EMAILS\\nPENDING FOLLOWUP"}\n  linkText="View all →"\n  linkTo="/email?section=followup"\n  heading="Recent Followups"\n>\n  {rows.map(r => <EmailRefRow key={r.id} {...r} />)}\n</KpiPanel>`}
      >
        <KpiPanel
          icon="envelope"
          num="12"
          numColor="var(--evo-cyan)"
          label={"EMAILS\nPENDING FOLLOWUP"}
          linkText="View all →"
          linkTo="/email?section=followup"
          heading="Recent Followups"
        >
          {[
            { from: 'cliente@empresa.com', subject: 'Re: Propuesta Q1' },
            { from: 'proveedor@supply.io', subject: 'Factura pendiente' },
            { from: 'lead@newbiz.com', subject: 'Seguimiento reunión' },
          ].map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: i < 2 ? '1px solid var(--evo-stroke)' : 'none', fontFamily: 'var(--evo-font)' }}>
              <span style={{ fontSize: 12, color: 'var(--evo-fg-3)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.from}</span>
              <span style={{ fontSize: 12, color: 'var(--evo-fg-2)', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subject}</span>
            </div>
          ))}
        </KpiPanel>
      </Example>

      <PropsTable rows={[
        { prop: 'icon', type: 'IconName', required: true, description: 'Ikonate icon rendered at 80px in column 1.' },
        { prop: 'num', type: 'string', required: true, description: 'Large display number (200px font) in column 2.' },
        { prop: 'numColor', type: 'string', required: true, description: 'CSS color applied to the icon and num.' },
        { prop: 'label', type: 'string', required: true, description: 'Uppercase label below the num. Use \\n for line breaks.' },
        { prop: 'linkText / linkTo', type: 'string', required: true, description: 'Cyan link at bottom-right of column 2.' },
        { prop: 'heading', type: 'string', required: true, description: 'Column 3 title above the rows list.' },
        { prop: 'rowsLinkText / rowsLinkTo', type: 'string', description: 'Optional secondary link at bottom of column 3.' },
        { prop: 'children', type: 'ReactNode', required: true, description: 'Row content for column 3 — typically fetched data rows.' },
      ]} />

      {/* ── ListItem ── */}
      <Label>ListItem</Label>

      <Example
        variant="A — email-style, unread"
        description="bold title + followup and category badges bottom-right"
        code={`<ListItem\n  title="noreply@empresa.com"\n  subtitle="Re: Proposal — Looks great, let's proceed"\n  meta="2h ago"\n  badges={[followupPill, categoryPill]}\n  unread\n/>`}
      >
        <ListItem
          title="noreply@empresa.com"
          subtitle="Re: Proposal review — Looks great, let's proceed with the next steps and schedule a call this week"
          meta="2h ago"
          badges={[followupBadge, categoryBadge]}
          unread
        />
      </Example>

      <Example
        variant="B — email-style, read"
        description="normal weight, no badges"
        code={`<ListItem\n  title="newsletter@medium.com"\n  subtitle="Weekly digest — Top stories from the past week"\n  meta="Yesterday"\n/>`}
      >
        <ListItem
          title="newsletter@medium.com"
          subtitle="Weekly digest — Top stories from the past week in tech and design"
          meta="Yesterday"
        />
      </Example>

      <Example
        variant="C — contact-style, alwaysShowActions"
        description="source badge + relationship dropdown always visible below subtitle"
        code={`<ListItem\n  title="Eduardo Carrasco"\n  subtitle="eduardo@evocreativestudio.com"\n  meta={<CategoryChip />}\n  actions={<><SourceBadge /><RelDropdown /></>}\n  alwaysShowActions\n/>`}
      >
        <ListItem
          title="Eduardo Carrasco"
          subtitle="eduardo@evocreativestudio.com"
          meta={<span style={{ background: 'var(--evo-cyan)22', color: 'var(--evo-cyan)', fontSize: '10px', padding: '1px 6px', borderRadius: 3, border: '1px solid var(--evo-cyan)44' }}>Clientes</span>}
          actions={
            <>
              <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', lineHeight: '16px', background: 'var(--evo-bg-2)', color: 'var(--evo-fg-3)', border: '1px solid var(--evo-stroke)' }}>
                manual
              </span>
              <Dropdown<string>
                value="cliente"
                options={[{ value: '', label: 'No relationship' }, { value: 'cliente', label: 'Cliente' }, { value: 'proveedor', label: 'Proveedor' }]}
                onChange={() => {}}
                size="xs"
              />
            </>
          }
          alwaysShowActions
        />
      </Example>

      <Example
        variant="D — active state"
        description="cyan left border + tinted bg — detail panel open"
        code={`<ListItem title="…" subtitle="…" meta="…" active />`}
      >
        <ListItem
          title="active@example.com"
          subtitle="This item is active — the detail panel is currently open for it"
          meta="5m ago"
          active
        />
      </Example>

      <Example
        variant="E — bulk selected (checkbox visible)"
        description="onSelect renders checkbox; click to toggle"
        code={`<ListItem\n  title="…"\n  selected={checked}\n  onSelect={setChecked}\n  onClick={openDetail}\n/>`}
      >
        <ListItem
          title="selected@example.com"
          subtitle="Click the checkbox on the left to toggle bulk-selection state"
          meta="1h ago"
          selected={selE}
          onSelect={setSelE}
          onClick={() => {}}
        />
      </Example>

      <Example
        variant="F — density variants"
        description="compact (8px) / default (10px) / comfortable (14px) vertical padding"
        code={`<ListItem density="compact" ... />\n<ListItem density="default" ... />\n<ListItem density="comfortable" ... />`}
      >
        <div>
          {(['compact', 'default', 'comfortable'] as const).map(d => (
            <div key={d}>
              <div style={{ padding: '4px 12px 2px', fontSize: '10px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                {d}
              </div>
              <ListItem
                title={`density="${d}" — ${d === 'compact' ? '8px' : d === 'default' ? '10px' : '14px'} vertical padding`}
                subtitle="Supporting subtitle line"
                meta="3h ago"
                density={d}
              />
            </div>
          ))}
        </div>
      </Example>

      <Example
        variant="G — no subtitle"
        description="single-line item"
        code={`<ListItem title="Solo title" meta="1h ago" />`}
      >
        <ListItem
          title="No subtitle — single line item"
          meta="1h ago"
        />
      </Example>

      <Example
        variant="H — overflow (hover for tooltip)"
        description="truncated title + subtitle via native title attribute"
        code={`<ListItem\n  title="very-long-email@extremely-long-domain-name.example.com"\n  subtitle="An extremely long subject line that will certainly overflow"\n/>`}
      >
        <div style={{ maxWidth: 360 }}>
          <ListItem
            title="very-long-email-address-that-exceeds-the-available-width@some-very-long-domain.example.com"
            subtitle="Subject: An extremely long description that will certainly overflow the available space and be truncated with an ellipsis"
            meta="now"
          />
        </div>
      </Example>

      <PropsTable rows={[
        { prop: 'title', type: 'string', required: true, description: 'Primary text. Bold (600) when unread=true. Cyan when active=true. Native title tooltip on overflow.' },
        { prop: 'subtitle', type: 'string', description: 'Secondary muted line (fg-3, 11px). Truncated with native title tooltip.' },
        { prop: 'meta', type: 'ReactNode', description: 'Top-right slot: timestamp string, score, or a badge chip.' },
        { prop: 'badges', type: 'ReactNode[]', description: 'Bottom-right pills stacked vertically. null/undefined entries are filtered.' },
        { prop: 'unread', type: 'boolean', description: 'Bolds the title (font-weight 600).' },
        { prop: 'active', type: 'boolean', description: 'Cyan left border + rgba(0,250,200,0.06) background.' },
        { prop: 'selected', type: 'boolean', description: 'Checkbox checked state for bulk selection.' },
        { prop: 'onSelect', type: '(selected: boolean) => void', description: 'Renders a checkbox. Called with the new checked value.' },
        { prop: 'onClick', type: '() => void', description: 'Row click handler. Sets cursor:pointer when defined.' },
        { prop: 'actions', type: 'ReactNode', description: 'Secondary actions slot. Hover-only by default; stopPropagation handled internally.' },
        { prop: 'alwaysShowActions', type: 'boolean', description: "Show actions inline below subtitle (contact-style). Default false = hover-only." },
        { prop: 'density', type: "'compact' | 'default' | 'comfortable'", description: "Vertical padding 8 / 10 / 14px. Default 'default'." },
      ]} />
    </ShowcaseSection>
  );
}

// ── DataTable ─────────────────────────────────────────────────────────────────

interface Lead {
  id: number;
  businessName: string;
  city: string;
  stage: string;
  score: number;
  createdAt: string;
}

const BIZ_NAMES  = ['Acme Corp', 'Green Valley', 'TechNova', 'Blue Ocean', 'Sunrise Digital',
  'Peak Solutions', 'Metro Agency', 'Coast Media', 'Nova Labs', 'Bright Ideas'];
const CITIES     = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Mar del Plata',
  'Salta', 'Tucumán', 'La Plata', 'Santiago', 'Lima'];
const STAGES     = ['new', 'contacted', 'qualified', 'proposal', 'closed'];

const MOCK_LEADS: Lead[] = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  businessName: (BIZ_NAMES[i % 10] ?? 'Unknown') + (i >= 10 ? ` ${Math.floor(i / 10) + 1}` : ''),
  city:         CITIES[i % 10]  ?? 'Unknown',
  stage:        STAGES[i % 5]   ?? 'new',
  score:        Math.round(40 + (i * 17 + 11) % 60),
  createdAt:    new Date(2025, 2, 1 + (i % 28)).toLocaleDateString('es'),
}));

const LEADS_COLUMNS: ColumnDef<Lead>[] = [
  { accessorKey: 'id',           header: '#',           size: 48 },
  { accessorKey: 'businessName', header: 'Business' },
  { accessorKey: 'city',         header: 'City' },
  { accessorKey: 'stage',        header: 'Stage',
    cell: ({ getValue }) => {
      const v = getValue<string>();
      const colors: Record<string, string> = {
        new: 'var(--evo-fg-3)', contacted: 'var(--evo-amber)',
        qualified: 'var(--evo-cyan)', proposal: 'var(--evo-blue-300)', closed: 'var(--evo-success)',
      };
      return <span style={{ color: colors[v] ?? 'inherit', fontWeight: 500 }}>{v}</span>;
    },
  },
  { accessorKey: 'score', header: 'Score',
    cell: ({ getValue }) => {
      const v = getValue<number>();
      return <span style={{ color: v >= 70 ? 'var(--evo-cyan)' : v >= 50 ? 'var(--evo-amber)' : 'var(--evo-fg-3)' }}>{v}</span>;
    },
  },
  { accessorKey: 'createdAt', header: 'Created' },
];

function DataTableSection() {
  const [selRows, setSelRows]   = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [density, setDensity]   = useState<'compact' | 'default' | 'comfortable'>('default');

  return (
    <ShowcaseSection
      id="datatable"
      title="DataTable"
      description="Headless table wrapper sobre @tanstack/react-table v8 — sorting, selection, pagination, density — packages/web/src/components/ui/DataTable.tsx"
    >
      <Label>Básico — sin selection, sin pagination</Label>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={MOCK_LEADS.slice(0, 5)}
      />

      <div style={{ height: 32 }} />
      <Label>Sorting — click en header para ordenar</Label>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={MOCK_LEADS.slice(0, 8)}
        sortable
      />

      <div style={{ height: 32 }} />
      <Label>Selection + callback</Label>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={MOCK_LEADS.slice(0, 6)}
        selectable
        onSelectionChange={setSelRows}
        getRowId={r => String(r.id)}
      />
      {selRows.length > 0 && (
        <div style={{
          marginTop: 8, padding: '8px 12px', background: 'var(--evo-cyan-12)',
          border: '1px solid var(--evo-cyan)', borderRadius: 'var(--evo-r)',
          fontSize: 12, color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font)',
        }}>
          {selRows.length} fila{selRows.length > 1 ? 's' : ''} seleccionada{selRows.length > 1 ? 's' : ''}: {selRows.map(r => r.businessName).join(', ')}
        </div>
      )}

      <div style={{ height: 32 }} />
      <Label>Pagination — pageSize 10</Label>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={MOCK_LEADS}
        pagination={{ pageSize: 10 }}
        sortable
      />

      <div style={{ height: 32 }} />
      <Label>Loading state</Label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Button variant="outline" tone="muted" size="sm" onClick={() => setIsLoading(v => !v)}>
          {isLoading ? 'Stop loading' : 'Start loading'}
        </Button>
      </div>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={isLoading ? [] : MOCK_LEADS.slice(0, 4)}
        loading={isLoading}
        emptyMessage="No leads found"
      />

      <div style={{ height: 32 }} />
      <Label>Empty state</Label>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={[]}
        emptyMessage="No leads match your filters"
      />

      <div style={{ height: 32 }} />
      <Label>Density — compact / default / comfortable</Label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(['compact', 'default', 'comfortable'] as const).map(d => (
          <Button
            key={d}
            variant={density === d ? 'solid' : 'outline'}
            tone={density === d ? 'cyan' : 'muted'}
            size="sm"
            onClick={() => setDensity(d)}
          >
            {d}
          </Button>
        ))}
      </div>
      <DataTable<Lead>
        columns={LEADS_COLUMNS}
        data={MOCK_LEADS.slice(0, 5)}
        density={density}
      />

      <PropsTable rows={[
        { prop: 'columns',           type: 'ColumnDef<T>[]',            required: true,  description: 'Definición de columnas de TanStack Table v8. Re-exportado desde DataTable.' },
        { prop: 'data',              type: 'T[]',                        required: true,  description: 'Datos de la tabla.' },
        { prop: 'size',              type: "'sm' | 'md'",                required: false, description: 'Font size de filas 12 / 13px. Default md.' },
        { prop: 'density',           type: "'compact' | 'default' | 'comfortable'", required: false, description: 'Padding vertical 6 / 10 / 14px. Default default.' },
        { prop: 'selectable',        type: 'boolean',                    required: false, description: 'Agrega columna de checkboxes. Default false.' },
        { prop: 'onSelectionChange', type: '(rows: T[]) => void',        required: false, description: 'Llamado cuando cambia la selección con el array de filas seleccionadas.' },
        { prop: 'sortable',          type: 'boolean',                    required: false, description: 'Click en headers para sort. Default true.' },
        { prop: 'pagination',        type: '{ pageSize: number } | false', required: false, description: 'Footer de paginación con Prev/Next. Default false = sin paginación.' },
        { prop: 'emptyMessage',      type: 'string',                     required: false, description: 'Texto cuando data está vacía. Default "No data".' },
        { prop: 'loading',           type: 'boolean',                    required: false, description: 'Overlay con spinner sobre la tabla.' },
        { prop: 'getRowId',          type: '(row: T) => string',         required: false, description: 'ID estable por fila — importante para selection correcta.' },
      ]} />
    </ShowcaseSection>
  );
}

// ── FeedbackStates ────────────────────────────────────────────────────────────

function FeedbackStatesSection() {
  return (
    <ShowcaseSection
      id="feedback"
      title="Feedback States"
      description="EmptyState y LoadingState — placeholders consistentes para listas vacías y estados de carga — packages/web/src/components/ui/EmptyState.tsx · LoadingState.tsx"
    >
      <Label>EmptyState — icon + title + description + action</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 9l6 6M15 9l-6 6" strokeLinecap="round" />
            </svg>
          }
          title="No leads found"
          description="Try adjusting your filters or import a new list to get started."
          action={<Button variant="outline" tone="muted" size="sm">Import leads</Button>}
        />
      </div>

      <div style={{ height: 24 }} />
      <Label>EmptyState — sin icon, title + description</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <EmptyState
          title="No contacts"
          description="Import a CSV or add contacts manually from the Contacts page."
        />
      </div>

      <div style={{ height: 24 }} />
      <Label>EmptyState — solo title</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <EmptyState title="Nothing here yet." />
      </div>

      <div style={{ height: 24 }} />
      <Label>EmptyState — size sm (max-width 320px)</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', maxWidth: 320 }}>
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
            </svg>
          }
          title="No drafts"
          size="sm"
        />
      </div>

      <div style={{ height: 24 }} />
      <Label>EmptyState — reserveSpace=true (size sm)</Label>
      <div style={{ border: '1px dashed var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <EmptyState title="No results found" size="sm" reserveSpace />
      </div>

      <div style={{ height: 32 }} />
      <Label>LoadingState — default (md, label common.loading)</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <LoadingState />
      </div>

      <div style={{ height: 24 }} />
      <Label>LoadingState — label personalizado</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <LoadingState label="Loading leads..." />
      </div>

      <div style={{ height: 24 }} />
      <Label>LoadingState — sm / md / lg lado a lado</Label>
      <div style={{
        border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)',
        display: 'flex', gap: 'var(--evo-space-6)', justifyContent: 'center',
        padding: 'var(--evo-space-5)',
      }}>
        <LoadingState size="sm" label="sm" />
        <LoadingState size="md" label="md" />
        <LoadingState size="lg" label="lg" />
      </div>

      <div style={{ height: 24 }} />
      <Label>LoadingState — fullHeight=true</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
        <LoadingState label="Loading contacts..." fullHeight />
      </div>
    </ShowcaseSection>
  );
}

// ── List Pattern Recipe ────────────────────────────────────────────────────────

interface DemoTask {
  id: string;
  title: string;
  assignee: string;
  status: 'open' | 'in-progress' | 'done';
  timestamp: string;
}

const DEMO_STATUS_TONE = {
  open: 'steel',
  'in-progress': 'cyan',
  done: 'success',
} as const;

const DEMO_STATUS_LABEL: Record<DemoTask['status'], string> = {
  open: 'Open',
  'in-progress': 'In Progress',
  done: 'Done',
};

function makeDemoTasks(): DemoTask[] {
  const now = Date.now();
  const H = 3_600_000;
  const D = 86_400_000;
  return [
    { id: 't1', title: 'Review proposal deck', assignee: 'Maria S.', status: 'in-progress', timestamp: new Date(now - 2 * H).toISOString() },
    { id: 't2', title: 'Send contract to client', assignee: 'Eduardo C.', status: 'open', timestamp: new Date(now - 5 * H).toISOString() },
    { id: 't3', title: 'Follow up on invoice #0042', assignee: 'Maria S.', status: 'open', timestamp: new Date(now - 1.5 * D).toISOString() },
    { id: 't4', title: 'Update CRM records', assignee: 'Eduardo C.', status: 'done', timestamp: new Date(now - 3 * D).toISOString() },
    { id: 't5', title: 'Plan sprint retrospective', assignee: 'Maria S.', status: 'open', timestamp: new Date(now - 4 * D).toISOString() },
  ];
}

const LIST_RECIPE_SNIPPET = `// 1. State
const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
const allSelected = selectedIds.size === items.length && items.length > 0;
const someSelected = selectedIds.size > 0 && !allSelected;

// 2. Toolbar
<ListToolbar
  allSelected={allSelected}
  someSelected={someSelected}
  onSelectAll={(sel) =>
    setSelectedIds(sel ? new Set(items.map(i => i.id)) : new Set())
  }
  onExpandAll={expandAll}
  onCollapseAll={collapseAll}
/>

// 3. Banded list
<TimeBandedList<MyItem, string>
  items={items}
  getTimestamp={(item) => item.createdAt}
  getId={(item) => item.id}
  checkedIds={selectedIds}
  onBandCheck={(ids, allChecked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allChecked) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }}
  renderItem={(item) => (
    <ListItem
      itemId={item.id}
      title={item.title}
      subtitle={item.assignee}
      meta={formatDate(item.createdAt)}
      badges={[
        <Pill key="status" size="xs" tone={statusTone(item.status)}>
          {t('status.' + item.status)}
        </Pill>,
      ]}
      selected={selectedIds.has(item.id)}
      onSelect={(sel) => toggleSelect(item.id, sel)}
      active={activeId === item.id}
      onClick={() => setActiveId(item.id)}
    />
  )}
/>`;

function ListPatternSection() {
  const [demoTasks] = useState<DemoTask[]>(makeDemoTasks);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const allSelected = selectedIds.size === demoTasks.length && demoTasks.length > 0;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleBandCheck = (ids: string[], allChecked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allChecked) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  return (
    <ShowcaseSection
      id="list-pattern"
      title="List Pattern Recipe"
      description="ListToolbar + TimeBandedList + ListItem + Pill xs — the standard pattern for all list modules in EvoHub."
    >
      <Label>Demo — interactive</Label>
      <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
        <ListToolbar
          allSelected={allSelected}
          someSelected={someSelected}
          onSelectAll={(sel) => setSelectedIds(sel ? new Set(demoTasks.map(t => t.id)) : new Set())}
        />
        <TimeBandedList<DemoTask, string>
          items={demoTasks}
          getTimestamp={(t) => t.timestamp}
          getId={(t) => t.id}
          checkedIds={selectedIds}
          onBandCheck={handleBandCheck}
          renderItem={(task) => (
            <ListItem
              itemId={task.id}
              title={task.title}
              subtitle={task.assignee}
              badges={[
                <Pill key="status" size="xs" tone={DEMO_STATUS_TONE[task.status]}>
                  {DEMO_STATUS_LABEL[task.status]}
                </Pill>,
              ]}
              selected={selectedIds.has(task.id)}
              onSelect={(sel) => {
                setSelectedIds(prev => {
                  const next = new Set(prev);
                  sel ? next.add(task.id) : next.delete(task.id);
                  return next;
                });
              }}
            />
          )}
        />
      </div>

      <div style={{ height: 24 }} />
      <Label>Copy-paste recipe</Label>
      <div style={{
        background: 'var(--evo-bg-3)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        padding: '16px 20px',
        overflowX: 'auto',
      }}>
        <pre style={{
          margin: 0,
          fontSize: '11px',
          lineHeight: 1.6,
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font-mono)',
          whiteSpace: 'pre',
        }}>{LIST_RECIPE_SNIPPET}</pre>
      </div>

      <p style={{
        margin: '16px 0 0',
        fontSize: '11px',
        color: 'var(--evo-fg-4)',
        fontFamily: 'var(--evo-font)',
      }}>
        Para detalles completos del pattern, ver{' '}
        <span style={{ color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font-mono)' }}>/DESIGN_SYSTEM.md</span>{' '}
        en la raíz del repo.
      </p>
    </ShowcaseSection>
  );
}

// ── Detail Page Pattern ───────────────────────────────────────────────────────

function DetailPageSection() {
  const [lead, setLead] = useState({
    website: 'acme.com',
    phone: '+1 555 0100',
    email: 'contact@acme.com',
    industry: 'SaaS / B2B',
    address: '',
  });
  const [failNext, setFailNext] = useState(false);

  const save = (field: keyof typeof lead) => async (v: string) => {
    if (failNext) {
      setFailNext(false);
      throw new Error('Server error 503: service unavailable');
    }
    await new Promise<void>(r => setTimeout(r, 400));
    setLead(prev => ({ ...prev, [field]: v }));
  };

  return (
    <ShowcaseSection
      id="detail-page"
      title="Detail Page Pattern"
      description="DetailSection + KVGrid + KVRow — estructura canónica para páginas de detalle. Ver /DESIGN_SYSTEM.md § Detail Page Pattern."
    >
      {/* A — DetailSection */}
      <Label>A — DetailSection</Label>
      <Example
        variant="sin divider / con divider / con titleRight"
        code={`<DetailSection title="Sección 1" divider={false}>…</DetailSection>
<DetailSection title="Sección 2">…</DetailSection>
<DetailSection title="Con acción" titleRight={<Button size="sm">Edit</Button>}>…</DetailSection>`}
      >
        <div style={{ maxWidth: 560 }}>
          <DetailSection title="Primera sección (divider=false)" divider={false}>
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text)' }}>
              Sin borderTop — para la primera sección de cada columna.
            </p>
          </DetailSection>
          <DetailSection title="Segunda sección (divider=true)">
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text)' }}>
              Las siguientes secciones llevan borderTop automático.
            </p>
          </DetailSection>
          <DetailSection
            title="Con titleRight"
            titleRight={<Button variant="ghost" tone="muted" size="sm">Edit all</Button>}
          >
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text)' }}>
              El slot titleRight acepta cualquier ReactNode.
            </p>
          </DetailSection>
        </div>
      </Example>

      {/* B — KVGrid + KVRow read-only */}
      <Label>B — KVGrid + KVRow (read-only)</Label>
      <Example
        variant="1 columna, 4 rows"
        code={`<KVGrid>
  <KVRow label="Sitio web" value="acme.com" />
  <KVRow label="Teléfono" value="+1 555 0100" />
  <KVRow label="Email" value="contact@acme.com" />
  <KVRow label="Industria" value="SaaS / B2B" last />
</KVGrid>`}
      >
        <div style={{ maxWidth: 480 }}>
          <KVGrid>
            <KVRow label="Sitio web" value="acme.com" />
            <KVRow label="Teléfono" value="+1 555 0100" />
            <KVRow label="Email" value="contact@acme.com" />
            <KVRow label="Industria" value="SaaS / B2B" last />
          </KVGrid>
        </div>
      </Example>
      <Example
        variant="2 columnas"
        code={`<KVGrid columns={2}>
  <KVRow label="Sitio web" value="acme.com" />
  …6 rows total…
</KVGrid>`}
      >
        <div style={{ maxWidth: 700 }}>
          <KVGrid columns={2}>
            <KVRow label="Sitio web" value="acme.com" />
            <KVRow label="Teléfono" value="+1 555 0100" />
            <KVRow label="Email" value="contact@acme.com" />
            <KVRow label="Industria" value="SaaS / B2B" />
            <KVRow label="Empleados" value="50–200" />
            <KVRow label="País" value="United States" last />
          </KVGrid>
        </div>
      </Example>
      <Example
        variant="renderValue custom + value null con placeholder"
        code={`<KVRow label="Website" value="https://acme.com"
  renderValue={v => <a href={v}>{v.replace(/^https?:\\/\\//, '')}</a>}
/>
<KVRow label="Notas" value={null} placeholder="Sin notas" last />`}
      >
        <div style={{ maxWidth: 480 }}>
          <KVGrid>
            <KVRow
              label="Website"
              value="https://acme.com"
              renderValue={v => (
                <a href={v} target="_blank" rel="noreferrer" style={{ color: 'var(--evo-cyan)' }}>
                  {v.replace(/^https?:\/\//, '')}
                </a>
              )}
            />
            <KVRow label="Notas" value={null} placeholder="Sin notas" last />
          </KVGrid>
        </div>
      </Example>

      {/* C — KVRow editable */}
      <Label>C — KVRow editable (hover para ver el lápiz)</Label>
      <Example
        variant="text · email con validación · url con validación"
        code={`<KVRow
  label="Email"
  value={email}
  type="email"
  validate={v => /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(v) ? null : 'Email inválido'}
  onSave={async v => save(v)}
/>`}
      >
        <div style={{ maxWidth: 520 }}>
          <KVGrid>
            <KVRow
              label="Sitio web"
              value={lead.website}
              type="url"
              validate={v => {
                const url = v.startsWith('http') ? v : `https://${v}`;
                try { new URL(url); return null; } catch { return 'URL inválida'; }
              }}
              renderValue={v => <span style={{ color: 'var(--evo-cyan)' }}>{v}</span>}
              onSave={save('website')}
            />
            <KVRow
              label="Teléfono"
              value={lead.phone}
              type="tel"
              onSave={save('phone')}
            />
            <KVRow
              label="Email"
              value={lead.email}
              type="email"
              validate={v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Email inválido'}
              onSave={save('email')}
            />
            <KVRow
              label="Dirección"
              value={lead.address}
              placeholder="Sin dirección"
              validate={v => v.trim().length > 0 ? null : 'Campo requerido'}
              onSave={save('address')}
              last
            />
          </KVGrid>
        </div>
      </Example>
      <Example
        variant="simulación de error de save"
        description="Activá el toggle y luego intentá guardar para ver el error inline + toast"
        code={`onSave={async v => { throw new Error('Server error 503'); }}`}
      >
        <div style={{ maxWidth: 520 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Toggle checked={failNext} onChange={setFailNext} />
            <span style={{
              fontSize: 'var(--evo-fs-label)',
              color: failNext ? 'var(--evo-danger)' : 'var(--evo-text-muted)',
              fontFamily: 'var(--evo-font)',
            }}>
              {failNext ? 'Próximo save fallará — intentá guardar' : 'Simular error de save'}
            </span>
          </div>
          <KVGrid>
            <KVRow
              label="Industria"
              value={lead.industry}
              onSave={save('industry')}
              last
            />
          </KVGrid>
        </div>
      </Example>

      {/* D — Mini demo de página de detalle completa */}
      <Label>D — Mini demo: Detail Page completo</Label>
      <Example
        variant="SubNavBar + grid 3fr/2fr + DetailSection + KVGrid + Pill"
        fullWidth
        code={`// SubNavBar
<div style={{ display:'flex', height:'var(--evo-subnav-h)', padding:'0 var(--evo-content-pad-x)', borderBottom:'1px solid var(--evo-stroke)' }}>
  <Button variant="ghost" tone="muted" size="sm">← Volver</Button>
  <Pill variant="outline" tone="cyan" size="sm">Qualified</Pill>
</div>
// Grid 3fr/2fr
<div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:48 }}>
  <div>
    <DetailSection title="Business Snapshot" divider={false}><KVGrid>…</KVGrid></DetailSection>
    <DetailSection title="Resumen ejecutivo"><p>…</p></DetailSection>
  </div>
  <div>
    <DetailSection title="Clasificación" divider={false}><KVGrid>…</KVGrid></DetailSection>
  </div>
</div>`}
      >
        <div style={{ background: 'var(--evo-bg)', borderRadius: 'var(--evo-r)' }}>
          {/* SubNavBar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 'var(--evo-subnav-h)',
            padding: '0 var(--evo-space-5)',
            borderBottom: '1px solid var(--evo-stroke)',
          }}>
            <Button variant="ghost" tone="muted" size="sm">← Volver</Button>
            <div style={{ width: 1, height: 16, background: 'var(--evo-stroke)' }} />
            <Pill variant="outline" tone="cyan" size="sm">Qualified</Pill>
            <div style={{ marginLeft: 'auto' }}>
              <Button variant="outline" tone="muted" size="sm">Acciones</Button>
            </div>
          </div>
          {/* Grid 3fr / 2fr */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '3fr 2fr',
            gap: 48,
            padding: '24px var(--evo-space-5)',
            maxWidth: 900,
          }}>
            {/* Columna izquierda */}
            <div>
              <DetailSection title="Business Snapshot" divider={false}>
                <KVGrid>
                  <KVRow
                    label="Website"
                    value={lead.website}
                    type="url"
                    renderValue={v => <span style={{ color: 'var(--evo-cyan)' }}>{v}</span>}
                    onSave={save('website')}
                  />
                  <KVRow
                    label="Teléfono"
                    value={lead.phone}
                    type="tel"
                    onSave={save('phone')}
                  />
                  <KVRow
                    label="Email"
                    value={lead.email}
                    type="email"
                    validate={v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Email inválido'}
                    onSave={save('email')}
                    last
                  />
                </KVGrid>
              </DetailSection>
              <DetailSection title="Resumen ejecutivo">
                <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', lineHeight: 1.65, color: 'var(--evo-text)' }}>
                  Acme Corp es una empresa de SaaS B2B con presencia fuerte en el mercado mid-market.
                  Tienen presencia digital activa y señales claras de crecimiento reciente.
                </p>
              </DetailSection>
            </div>
            {/* Columna derecha */}
            <div>
              <DetailSection title="Clasificación" divider={false}>
                <KVGrid>
                  <KVRow
                    label="Prioridad"
                    value="alta"
                    renderValue={() => <Pill variant="outline" tone="amber" size="sm">Alta</Pill>}
                  />
                  <KVRow
                    label="Industria"
                    value={lead.industry}
                    onSave={save('industry')}
                    last
                  />
                </KVGrid>
              </DetailSection>
            </div>
          </div>
        </div>
      </Example>
    </ShowcaseSection>
  );
}

// ── Left nav ──────────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  {
    label: 'Tokens',
    items: [
      { id: 'colors', label: 'Colors' },
      { id: 'typography', label: 'Typography' },
      { id: 'spacing', label: 'Spacing' },
    ],
  },
  {
    label: 'Layout',
    items: [
      { id: 'appshell', label: 'AppShell' },
      { id: 'topbar', label: 'TopBar' },
      { id: 'sidebar', label: 'Sidebar' },
      { id: 'appfooter', label: 'AppFooter' },
    ],
  },
  {
    label: 'Module Chrome',
    items: [
      { id: 'module-chrome', label: 'Dropdown' },
      { id: 'module-chrome', label: 'FilterRow' },
      { id: 'module-chrome', label: 'ActionRow' },
      { id: 'module-chrome', label: 'SelectionActionBar' },
      { id: 'module-chrome', label: 'Tabs' },
    ],
  },
  {
    label: 'Primitives',
    items: [
      { id: 'primitives', label: 'Button + IconButton' },
      { id: 'primitives', label: 'Pill' },
      { id: 'primitives', label: 'Input + Textarea' },
      { id: 'primitives', label: 'Radio + Toggle' },
      { id: 'primitives', label: 'Select' },
      { id: 'primitives', label: 'DatePicker' },
    ],
  },
  {
    label: 'Overlays',
    items: [
      { id: 'overlays', label: 'Modal' },
      { id: 'overlays', label: 'Drawer' },
      { id: 'overlays', label: 'Toast' },
      { id: 'overlays', label: 'ConfirmDialog' },
    ],
  },
  {
    label: 'Data Display',
    items: [
      { id: 'data-display', label: 'Badge' },
      { id: 'data-display', label: 'Avatar' },
      { id: 'data-display', label: 'KpiPanel' },
      { id: 'data-display', label: 'ListItem' },
    ],
  },
  {
    label: 'Tables',
    items: [
      { id: 'datatable', label: 'DataTable' },
    ],
  },
  {
    label: 'Feedback',
    items: [
      { id: 'feedback', label: 'EmptyState / LoadingState' },
    ],
  },
  {
    label: 'Patterns',
    items: [
      { id: 'list-pattern', label: 'List Pattern' },
      { id: 'detail-page', label: 'Detail Page' },
    ],
  },
];

function ShowcaseNav() {
  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div style={{ width: 160, flexShrink: 0, paddingTop: 36, alignSelf: 'flex-start', position: 'sticky', top: 0 }}>
      {NAV_GROUPS.map(group => (
        <div key={group.label} style={{ marginBottom: 24 }}>
          <p style={{
            margin: '0 0 6px',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.88px',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-4)',
            fontFamily: 'var(--evo-font)',
          }}>{group.label}</p>
          {group.items.map(item => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '5px 8px',
                fontSize: '13px',
                fontFamily: 'var(--evo-font)',
                color: 'var(--evo-fg-2)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 'var(--evo-r)',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--evo-fg-1)'; e.currentTarget.style.background = 'var(--evo-bg-2)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--evo-fg-2)'; e.currentTarget.style.background = 'transparent'; }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function ShowcasePage() {
  const { setOverride } = useTopBarCtx();

  useEffect(() => {
    setOverride({ title: 'Showcase', heroIcon: 'settings-large', heroIconColor: 'var(--evo-fg-3)' });
    return () => setOverride({});
  }, [setOverride]);

  return (
    <div style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '10px var(--evo-content-pad-x)',
        borderBottom: '1px solid var(--evo-stroke)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <span style={{ fontSize: '11px', letterSpacing: '0.88px', textTransform: 'uppercase', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
          EvoHub Design System
        </span>
        <span style={{ color: 'var(--evo-stroke)' }}>·</span>
        <span style={{ fontSize: '11px', color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font-mono)' }}>
          packages/tokens/tokens.css
        </span>
        <span style={{ color: 'var(--evo-stroke)' }}>·</span>
        <span style={{ fontSize: '11px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
          single source of truth — edit there, HMR propagates everywhere
        </span>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ height: '100%', overflowY: 'auto', display: 'flex', gap: 0, paddingLeft: 'var(--evo-content-pad-x)' }}>
          <ShowcaseNav />
          <main style={{ flex: 1, paddingLeft: 40, paddingRight: 'var(--evo-content-pad-x)', paddingBottom: 120 }}>
            <ColorsSection />
            <TypographySection />
            <SpacingSection />
            <AppShellSection />
            <TopBarSection />
            <SidebarSection />
            <AppFooterSection />
            <ModuleChromeSection />
            <PrimitivesSection />
            <OverlaysSection />
            <DataDisplaySection />
            <DataTableSection />
            <FeedbackStatesSection />
            <ListPatternSection />
            <DetailPageSection />
          </main>
        </div>
      </div>
    </div>
  );
}
